import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  Navigate as RouterNavigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  type LinkProps,
  type NavLinkProps,
  type NavigateProps,
  type To,
} from "react-router-dom";
import { Latex } from "./components/Latex";
import {
  LaboratoryControls,
  LaboratoryLayout,
  LaboratoryLevelNavigation,
  LaboratoryToolbar,
  LaboratoryWorkbench,
  LaboratoryWorkspace,
} from "./components/LaboratoryLayout";
import {
  FactorizationWarningDialog,
  useSquareFactorization,
} from "./components/SquareFactorization";
import { SquareWorkbench } from "./components/SquareWorkbench";
import { SINGLE_LOCALE } from "./config";
import { news, newsBySlug, type NewsArticle } from "./content/news";
import { familyProof } from "./content/familyProofs";
import {
  MAGIC3_LATEX,
  commonProofById,
  commonProofs,
} from "./content/proofs";
import {
  FAMILIES,
  FIVE_FAMILIES,
  FOUR_FAMILIES,
  familyGroupLabel,
  familyOrbitDescription,
  familySummary,
  findFamilyById,
  justificationLabel,
  type FamilyDefinition,
  type FamilyLevel,
  type ParameterStrings,
} from "./lib/families";
import {
  createSnapshot,
  divideCoordinates,
  formatInteger,
  greatestCommonDivisor,
  greatestSquareDivisor,
  minimizeCoordinates,
  type Coordinates,
  type Position,
} from "./lib/magicSquare";
import { familyParameterGuide } from "./lib/parameterGuides";
import { orbitPath, siteOriginForLocale } from "./seo";
import {
  LocaleProvider,
  isLocale,
  localePath,
  preferredLocale,
  switchLocalePath,
  useLocale,
  type Locale,
} from "./i18n";
import { TimelinePage } from "./TimelinePage";
import { SixNineLabPage } from "./SixNineLab";
import { ResiduesTheoryPage, TheoryIndexPage } from "./TheoryPages";
import { PrimeDivisorsTheoryPage } from "./PrimeDivisorsTheory";
import { DirProgressionsTheoryPage } from "./DirProgressionsTheory";
import { SixNinePatternsTheoryPage } from "./SixNinePatternsTheory";
import { SixNineAtlasPage } from "./SixNineAtlasPage";
import { SevenNinePatternsTheoryPage } from "./SevenNinePatternsTheory";
import { SevenNineAtlasPage } from "./SevenNineAtlasPage";
import { FmnTfmnTheoryPage } from "./FmnTfmnTheory";
import { EarlyTfFamiliesTheoryPage } from "./EarlyTfFamiliesTheory";
import { F7PlusTheoryPage } from "./F7PlusTheory";
import { TfmnContainerFormsTheoryPage } from "./TfmnContainerFormsTheory";
import { F4PlusTheoryPage } from "./F4PlusTheory";
import { TfPairGenerationTheoryPage } from "./TfPairGenerationTheory";
import { F9PlusTheoryPage } from "./F9PlusTheory";
import { F9EllipticLayersTheoryPage } from "./F9EllipticLayersTheory";
import { AbcdehSurfaceTheoryPage } from "./AbcdehSurfaceTheory";
import { AbcdejSurfaceTheoryPage } from "./AbcdejSurfaceTheory";
import { AbcdfhSurfaceTheoryPage } from "./AbcdfhSurfaceTheory";
import { AbcdhjSurfaceTheoryPage } from "./AbcdhjSurfaceTheory";
import { AbceCommonSurfaceTheoryPage } from "./AbceCommonSurfaceTheory";
import { AbdefhSurfaceTheoryPage } from "./AbdefhSurfaceTheory";
import { AbdefjSurfaceTheoryPage } from "./AbdefjSurfaceTheory";
import { AbefghSurfaceTheoryPage } from "./AbefghSurfaceTheory";
import { AbcdefSurfaceTheoryPage } from "./AbcdefSurfaceTheory";
import { ParallelTfmnKummerTheoryPage } from "./ParallelTfmnKummerTheory";
import { AbcdegSurfaceTheoryPage } from "./AbcdegSurfaceTheory";
import { AbcdfgSurfaceTheoryPage } from "./AbcdfgSurfaceTheory";
import { AbcdgjSurfaceTheoryPage } from "./AbcdgjSurfaceTheory";
import { AbcghjSurfaceTheoryPage } from "./AbcghjSurfaceTheory";
import { SixNineSynthesisTheoryPage } from "./SixNineSynthesisTheory";
import { EllipticSurfacesPrimerTheoryPage } from "./EllipticSurfacesPrimerTheory";
import { SemimagicAlgebraTheoryPage } from "./SemimagicAlgebraTheory";
import { SemimagicStructureTheoryPage } from "./SemimagicStructureTheory";

const PARAMETER_KEYS = ["a", "b", "c", "d"] as const;
type FamilyRelation = "exact" | "derived";

interface MinimizationNotice {
  kind: "preserved" | "primitive" | "derived" | "unavailable";
  dividedBy: bigint;
  remainingDivisor: bigint;
}

interface SquarePreservingReduction {
  coordinates: Coordinates;
  dividedBy: bigint;
  remainingDivisor: bigint;
}

function squarePreservingReduction(
  coordinates: Coordinates,
): SquarePreservingReduction | null {
  const divisor = greatestCommonDivisor(...coordinates);
  if (divisor < 2n) {
    return {
      coordinates,
      dividedBy: 1n,
      remainingDivisor: 1n,
    };
  }
  const squareDivisor = greatestSquareDivisor(divisor);
  if (squareDivisor === null) return null;
  return {
    coordinates: divideCoordinates(coordinates, squareDivisor),
    dividedBy: squareDivisor,
    remainingDivisor: divisor / squareDivisor,
  };
}

const CELL_FORM_LATEX: Readonly<Record<string, string>> = {
  A: "E+x",
  B: "E-x+y",
  C: "E-y",
  D: "E-x-y",
  E: "E",
  F: "E+x+y",
  G: "E+y",
  H: "E+x-y",
  J: "E-x",
};

function localizeTo(locale: Locale, to: To): To {
  if (typeof to === "string") return localePath(locale, to);
  return {
    ...to,
    pathname: to.pathname ? localePath(locale, to.pathname) : to.pathname,
  };
}

function Link({ to, ...props }: LinkProps) {
  const { locale } = useLocale();
  return <RouterLink to={localizeTo(locale, to)} {...props} />;
}

function NavLink({ to, ...props }: NavLinkProps) {
  const { locale } = useLocale();
  return <RouterNavLink to={localizeTo(locale, to)} {...props} />;
}

function Navigate({ to, ...props }: NavigateProps) {
  const { locale } = useLocale();
  return <RouterNavigate to={localizeTo(locale, to)} {...props} />;
}

function AppMark() {
  return (
    <span className="app-mark" aria-hidden="true">
      {Array.from({ length: 9 }, (_, index) => (
        <i key={index} />
      ))}
    </span>
  );
}

function localeRedirectTarget(locale: Locale, pathname: string): string {
  return `${localePath(locale, pathname)}${window.location.search}${window.location.hash}`;
}

function LocaleLayout() {
  const { locale: rawLocale } = useParams();
  const location = useLocation();
  if (SINGLE_LOCALE) {
    return (
      <LocaleProvider locale={SINGLE_LOCALE}>
        <AppShell />
      </LocaleProvider>
    );
  }
  if (!isLocale(rawLocale)) {
    const locale = preferredLocale();
    return (
      <RouterNavigate
        replace
        to={localeRedirectTarget(locale, location.pathname)}
      />
    );
  }
  return (
    <LocaleProvider locale={rawLocale}>
      <AppShell />
    </LocaleProvider>
  );
}

function LegacyLocaleRedirect() {
  const location = useLocation();
  if (SINGLE_LOCALE) {
    return (
      <LocaleProvider locale={SINGLE_LOCALE}>
        <NotFoundPage />
      </LocaleProvider>
    );
  }
  const locale = preferredLocale();
  return (
    <RouterNavigate
      replace
      to={localeRedirectTarget(locale, location.pathname)}
    />
  );
}

function LanguageSwitcher() {
  const { locale, text } = useLocale();
  const location = useLocation();
  if (SINGLE_LOCALE) return null;
  return (
    <nav className="language-switcher" aria-label={text("Язык", "Language")}>
      {(["ru", "en"] as const).map((candidate) => (
        <RouterLink
          aria-current={candidate === locale ? "page" : undefined}
          className={candidate === locale ? "active" : ""}
          key={candidate}
          lang={candidate}
          onClick={() =>
            window.localStorage.setItem("magic-squares-locale", candidate)
          }
          to={`${switchLocalePath(location.pathname, candidate)}${location.search}${location.hash}`}
        >
          {candidate.toUpperCase()}
        </RouterLink>
      ))}
    </nav>
  );
}

function AppShell() {
  const { text } = useLocale();
  return (
    <div className="app-shell">
      <header className="site-header">
        <Link
          className="brand"
          to="/"
          aria-label={text("Magic Squares — на главную", "Magic Squares — home")}
        >
          <AppMark />
          <span>
            <strong>Magic Squares</strong>
            <small>proof atlas</small>
          </span>
        </Link>
        <nav
          className="main-nav"
          aria-label={text("Основная навигация", "Primary navigation")}
        >
          <NavLink to="/lab">{text("Лаборатория", "Laboratory")}</NavLink>
          <NavLink to="/theory">
            {text("Теория", "Theory")}
          </NavLink>
          <NavLink to="/news">{text("Новости", "News")}</NavLink>
          <NavLink to="/timeline">{text("Хронология", "Timeline")}</NavLink>
          <NavLink to="/about">{text("О проекте", "About")}</NavLink>
        </nav>
        <div className="header-meta">
          <LanguageSwitcher />
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="footer-copyright">
          <strong>
            © 2021–2026 {text("Владимир Мынка", "Vladimir Mynka")}
          </strong>
          <span>
            {text(
              "Распространение материалов разрешено с явным указанием авторства и активной ссылкой на сайт.",
              "Materials may be redistributed with explicit attribution and an active link to this site.",
            )}
          </span>
          <span>
            {text(
              "Обезличенные просмотры считаются на этом сервере без cookies и идентификаторов посетителей.",
              "Anonymous page counts are processed on this server without cookies or visitor identifiers.",
            )}{" "}
            <a href="https://db-ip.com" target="_blank" rel="noreferrer">
              {text("IP-геолокация от DB-IP", "IP geolocation by DB-IP")}
            </a>
          </span>
        </div>
        <nav aria-label={text("Сведения о проекте", "Project information")}>
          <Link to="/timeline">{text("Хронология", "Timeline")}</Link>
          <Link to="/about#ai-use">
            {text("Использование ИИ", "Use of AI")}
          </Link>
          <Link to="/about#copyright">
            {text("Авторство и условия", "Attribution and terms")}
          </Link>
        </nav>
      </footer>
    </div>
  );
}

function HomePage() {
  const { text } = useLocale();
  const researchStates = [
    {
      ratio: "4/9",
      status: text("полная классификация", "complete classification"),
      description: text(
        "23 орбиты квадратных масок и параметрические семейства.",
        "23 square-pattern orbits and their parametric families.",
      ),
    },
    {
      ratio: "5/9",
      status: text("полная классификация", "complete classification"),
      description: text(
        "23 орбиты квадратных масок и общие методы их параметризации.",
        "23 square-pattern orbits and general parametrization methods.",
      ),
    },
    {
      ratio: "6/9",
      status: text("исследование продолжается", "work in progress"),
      description: text(
        "Атлас позиционных типов, tfmn-классы и отдельные эллиптические поверхности.",
        "An atlas of positional types, tfmn classes, and individual elliptic surfaces.",
      ),
    },
    {
      ratio: "9/9",
      status: text("открытая задача", "open problem"),
      description: text(
        "Неизвестны ни искомый квадрат, ни доказательство его невозможности.",
        "Neither the required square nor a proof of its impossibility is known.",
      ),
    },
  ] as const;

  return (
    <div className="page home-page">
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">
            {text(
              "Открытая математическая задача 9/9",
              "The open 9/9 problem",
            )}
          </p>
          <h1>
            {text(
              "Существует ли магический квадрат 3×3 из девяти различных полных квадратов?",
              "Does a 3×3 magic square of nine distinct perfect squares exist?",
            )}
          </h1>
          <p className="hero-lead">
            {text(
              "Искомый целочисленный квадрат должен состоять из положительных попарно различных квадратов целых чисел. Ни такой квадрат, ни доказательство его невозможности пока не известны.",
              "The required integral square must have nine positive, pairwise distinct perfect-square entries. Neither such a square nor a proof of its impossibility is currently known.",
            )}
          </p>
          <div className="hero-actions">
            <Link className="button button-primary" to="/squares-of-squares">
              {text("Постановка задачи", "Problem statement")} <span>→</span>
            </Link>
            <Link className="button button-ghost" to="/theory">
              {text("Материалы исследования", "Research materials")}
            </Link>
          </div>
        </div>
        <HeroSquare />
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">
              {text("Подход к задаче", "Approach to the problem")}
            </p>
            <h2>
              {text(
                "Частичные конфигурации",
                "Partial configurations",
              )}
            </h2>
          </div>
          <Link className="text-link" to="/squares-of-squares">
            {text("Определения", "Definitions")} <span>→</span>
          </Link>
        </div>
        <p className="home-section-introduction">
          {text(
            "Чтобы разделить позиционные и арифметические ограничения задачи 9/9, рассматриваются квадраты k/9, в которых квадратами являются только выбранные клетки. Классификации и параметризации этих масок не решают исходную задачу, но описывают её частичные случаи.",
            "To separate the positional and arithmetic restrictions of the 9/9 problem, the study considers k/9 squares in which only selected entries are required to be squares. Classifications and parametrizations of these patterns do not solve the original problem, but describe its partial cases.",
          )}
        </p>
        <div className="research-status-grid">
          {researchStates.map((item) => (
            <article className="research-status-card" key={item.ratio}>
              <strong>{item.ratio}</strong>
              <span>{item.status}</span>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">
              {text(
                "Результаты, возникшие по ходу исследования",
                "Results arising from the investigation",
              )}
            </p>
            <h2>{text("Побочные направления", "Related results")}</h2>
          </div>
        </div>
        <p className="home-section-introduction">
          {text(
            "Изучение частичных конфигураций привело к самостоятельным результатам, которые не сводятся к задаче 9/9.",
            "The study of partial configurations led to independent results that are not limited to the 9/9 problem.",
          )}
        </p>
        <div className="home-section-grid">
          <article className="home-section-card">
            <span>I</span>
            <h3>
              {text(
                "Квадратные классы и конгруэнтные числа",
                "Square classes and congruent numbers",
              )}
            </h3>
            <p>
              {text(
                "Функции f и tf, точная связь пар параметров с кривыми y²=x³−T²x и методы F4+, F7+ и F9+.",
                "The functions f and tf, the exact relation between parameter pairs and the curves y²=x³−T²x, and the F4+, F7+, and F9+ methods.",
              )}
            </p>
            <nav
              aria-label={text(
                "Квадратные классы и конгруэнтные числа",
                "Square classes and congruent numbers",
              )}
            >
              <Link to="/theory/f7-plus">F7+</Link>
              <Link to="/theory/f4-plus">F4+</Link>
              <Link to="/theory/f9-plus">F9+</Link>
            </nav>
          </article>
          <article className="home-section-card">
            <span>II</span>
            <h3>
              {text(
                "Эллиптические поверхности 6/9",
                "Elliptic surfaces for 6/9 patterns",
              )}
            </h3>
            <p>
              {text(
                "Квартики рода 1, их якобианы, ранги сечений и явные однопараметрические семейства.",
                "Genus-one quartics, their Jacobians, section ranks, and explicit one-parameter families.",
              )}
            </p>
            <nav
              aria-label={text(
                "Эллиптические поверхности 6/9",
                "Elliptic surfaces for 6/9 patterns",
              )}
            >
              <Link to="/theory/6-9/abcdeh">ABCDEH</Link>
              <Link to="/theory/6-9/abcdej">ABCDEJ</Link>
              <Link to="/theory/6-9/abcdfh">ABCDFH</Link>
              <Link to="/theory/6-9/abcdhj">ABCDHJ</Link>
              <Link to="/theory/6-9/abcegh-abcegj">ABCEGH / ABCEGJ</Link>
              <Link to="/theory/6-9/abdefh">ABDEFH</Link>
              <Link to="/theory/6-9/abdefj">ABDEFJ</Link>
              <Link to="/theory/6-9/abefgh">ABEFGH</Link>
              <Link to="/theory/6-9/abcdef">ABCDEF</Link>
            </nav>
          </article>
          <article className="home-section-card">
            <span>III</span>
            <h3>
              {text(
                "Алгебра магических матриц",
                "Algebra of magic matrices",
              )}
            </h3>
            <p>
              {text(
                "Пятимерная алгебра полумагических квадратов, её разложение и связь с расщеплёнными кватернионами.",
                "The five-dimensional algebra of semimagic squares, its decomposition, and its relation to split quaternions.",
              )}
            </p>
            <nav
              aria-label={text(
                "Алгебра магических матриц",
                "Algebra of magic matrices",
              )}
            >
              <Link to="/theory/matrix-algebra/magic-charming-semimagic">
                {text("Умножение", "Products")}
              </Link>
              <Link to="/theory/matrix-algebra/block-structure-split-quaternions">
                K⊕M₂(K)
              </Link>
            </nav>
          </article>
        </div>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{text("Материалы сайта", "Site contents")}</p>
            <h2>{text("Разделы", "Sections")}</h2>
          </div>
        </div>
        <div className="home-section-grid">
          <article className="home-section-card">
            <span>I</span>
            <h3>{text("Теория", "Theory")}</h3>
            <p>
              {text(
                "Последовательное изложение от общей формы магического квадрата до эллиптических поверхностей.",
                "A sequential account from the general form of a magic square to elliptic surfaces.",
              )}
            </p>
            <Link className="text-link" to="/theory">
              {text("Оглавление", "Contents")} <span>→</span>
            </Link>
          </article>
          <article className="home-section-card">
            <span>II</span>
            <h3>{text("Атласы масок", "Pattern atlases")}</h3>
            <p>
              {text(
                "Орбиты квадратных позиций с условиями, статусами и ссылками на выводы.",
                "Orbits of square positions with equations, statuses, and links to derivations.",
              )}
            </p>
            <nav aria-label={text("Атласы масок", "Pattern atlases")}>
              <Link to="/orbits/4">4/9</Link>
              <Link to="/orbits/5">5/9</Link>
              <Link to="/orbits/6">6/9</Link>
              <Link to="/orbits/7">7/9</Link>
            </nav>
          </article>
          <article className="home-section-card">
            <span>III</span>
            <h3>{text("Вычисления", "Computations")}</h3>
            <p>
              {text(
                "Калькуляторы для параметрических семейств, масок 6/9 и операций с магическими квадратами.",
                "Calculators for parametric families, 6/9 patterns, and operations on magic squares.",
              )}
            </p>
            <nav aria-label={text("Калькуляторы", "Calculators")}>
              <Link to="/lab">4/9–5/9</Link>
              <Link to="/lab/6">6/9</Link>
            </nav>
          </article>
        </div>
      </section>
    </div>
  );
}

function HeroSquare() {
  const { text } = useLocale();
  const values = [
    17689, 27889, 11449, 12769, 19009, 25249, 26569, 10129, 20329,
  ];
  const squareIndexes = new Set([0, 1, 2, 3, 6]);
  return (
    <div
      className="hero-visual"
      aria-label={text(
        "Частичный случай 5/9 с маской ABCDG",
        "A partial 5/9 case with the ABCDG pattern",
      )}
    >
      <div className="hero-square-label">
        <span>ABCDG</span>
        <small>{text("частичный случай 5/9", "a partial 5/9 case")}</small>
      </div>
      <div className="hero-square-grid">
        {values.map((value, index) => (
          <div
            className={squareIndexes.has(index) ? "is-square" : ""}
            key={value}
          >
            <span>{"ABCDEFGHJ"[index]}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
      <div
        className="hero-coordinates"
        aria-label={text("Координаты квадрата", "Square coordinates")}
      >
        <span>
          <small>E</small>19 009
        </span>
        <span>
          <small>x</small>−1 320
        </span>
        <span>
          <small>y</small>7 560
        </span>
      </div>
      <div className="proof-stamp">
        <span>5/9</span> {text("квадратов", "squares")}
      </div>
    </div>
  );
}

function LabPage({ routeFamilyId }: { routeFamilyId?: string } = {}) {
  const { locale, text } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedFamily = routeFamilyId ?? searchParams.get("family");
  const initialFamily = requestedFamily
    ? findFamilyById(requestedFamily) ?? null
    : null;
  let initialParameters = PARAMETER_KEYS.map(
    (key, index) =>
      searchParams.get(key) ?? (initialFamily ?? FAMILIES[0]).defaults[index],
  ) as unknown as ParameterStrings;
  let initialCoordinates: Coordinates;
  let initialNormalization = 1n;
  let initialFamilyRelation: FamilyRelation =
    initialFamily && searchParams.get("derived") === "1" ? "derived" : "exact";

  try {
    if (initialFamily) {
      const generated = initialFamily.generate(initialParameters);
      const requestedNormalization = BigInt(searchParams.get("norm") ?? "1");
      initialNormalization =
        requestedNormalization > 0n &&
        generated.every((value) => value % requestedNormalization === 0n)
          ? requestedNormalization
          : 1n;
      initialCoordinates = divideCoordinates(generated, initialNormalization);
    } else {
      initialCoordinates = [
        BigInt(searchParams.get("e") ?? "5"),
        BigInt(searchParams.get("x") ?? "3"),
        BigInt(searchParams.get("y") ?? "1"),
      ];
    }
  } catch {
    initialParameters = (initialFamily ?? FAMILIES[0]).defaults;
    initialNormalization = 1n;
    initialFamilyRelation = "exact";
    initialCoordinates = initialFamily
      ? initialFamily.generate(initialParameters)
      : [5n, 3n, 1n];
  }

  const [family, setFamily] = useState<FamilyDefinition | null>(initialFamily);
  const [catalogLevel, setCatalogLevel] = useState<FamilyLevel>(
    initialFamily?.level ?? (searchParams.get("level") === "4" ? 4 : 5),
  );
  const [parameters, setParameters] =
    useState<ParameterStrings>(initialParameters);
  const [coordinates, setCoordinates] =
    useState<Coordinates>(initialCoordinates);
  const [coordinateInputs, setCoordinateInputs] = useState<
    [string, string, string]
  >([
    initialCoordinates[0].toString(),
    initialCoordinates[1].toString(),
    initialCoordinates[2].toString(),
  ]);
  const [scale, setScale] = useState("−1");
  const [autoPreserveMinimize, setAutoPreserveMinimize] = useState(false);
  const [normalization, setNormalization] = useState(initialNormalization);
  const [familyRelation, setFamilyRelation] =
    useState<FamilyRelation>(initialFamilyRelation);
  const [minimizationNotice, setMinimizationNotice] =
    useState<MinimizationNotice | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const snapshot = useMemo(() => createSnapshot(coordinates), [coordinates]);
  const factorizationValues = useMemo(
    () => snapshot.cells.map((cell) => cell.value),
    [snapshot],
  );
  const squareFactorization = useSquareFactorization(factorizationValues);
  const parameterGuide = useMemo(
    () => (family ? familyParameterGuide(family, locale) : null),
    [family, locale],
  );
  const catalogFamilies = catalogLevel === 4 ? FOUR_FAMILIES : FIVE_FAMILIES;

  useEffect(() => {
    if (!family || window.location.hash !== "#family-proof") return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("family-proof")?.scrollIntoView();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [family]);

  useEffect(() => {
    const autoMinimize =
      window.localStorage.getItem("magic-squares-auto-preserve-minimize") ===
      "true";
    setAutoPreserveMinimize(autoMinimize);
    if (autoMinimize && family && familyRelation === "exact") {
      window.requestAnimationFrame(() => minimizePreservingMask());
    }
  }, []);

  function persistFamily(
    nextFamily: FamilyDefinition,
    nextParameters: ParameterStrings,
    nextNormalization = 1n,
    nextRelation: FamilyRelation = "exact",
  ) {
    const nextSearch = new URLSearchParams({
      family: nextFamily.id,
      ...Object.fromEntries(
        PARAMETER_KEYS.map((key, index) => [key, nextParameters[index]]),
      ),
    });
    if (nextNormalization > 1n) {
      nextSearch.set("norm", nextNormalization.toString());
    }
    if (nextRelation === "derived") {
      nextSearch.set("derived", "1");
    }
    navigate({
      pathname: localePath(locale, "/lab"),
      search: `?${nextSearch.toString()}`,
    });
  }

  function persistManual(nextCoordinates: Coordinates) {
    const nextSearch = new URLSearchParams({
      e: nextCoordinates[0].toString(),
      x: nextCoordinates[1].toString(),
      y: nextCoordinates[2].toString(),
    });
    navigate({
      pathname: localePath(locale, "/lab"),
      search: `?${nextSearch.toString()}`,
    });
  }

  function setCurrentCoordinates(
    nextCoordinates: Coordinates,
    nextFamily: FamilyDefinition | null,
  ) {
    setCoordinates(nextCoordinates);
    setCoordinateInputs([
      nextCoordinates[0].toString(),
      nextCoordinates[1].toString(),
      nextCoordinates[2].toString(),
    ]);
    setFamily(nextFamily);
    setMinimizationNotice(null);
    setError("");
  }

  function generateFamily(nextParameters = parameters, nextFamily = family) {
    if (!nextFamily) return;
    try {
      let nextCoordinates = nextFamily.generate(nextParameters);
      let nextNormalization = 1n;
      let nextNotice: MinimizationNotice | null = null;
      if (autoPreserveMinimize) {
        const reduction = squarePreservingReduction(nextCoordinates);
        if (reduction) {
          nextCoordinates = reduction.coordinates;
          nextNormalization = reduction.dividedBy;
          if (reduction.dividedBy > 1n || reduction.remainingDivisor > 1n) {
            nextNotice = {
              kind: "preserved",
              dividedBy: reduction.dividedBy,
              remainingDivisor: reduction.remainingDivisor,
            };
          }
        }
      }
      setCurrentCoordinates(nextCoordinates, nextFamily);
      setNormalization(nextNormalization);
      setFamilyRelation("exact");
      setMinimizationNotice(nextNotice);
      persistFamily(
        nextFamily,
        nextParameters,
        nextNormalization,
        "exact",
      );
    } catch {
      setError(
        text(
          "Параметры семейства должны быть целыми числами.",
          "Family parameters must be integers.",
        ),
      );
    }
  }

  function selectFamily(nextFamily: FamilyDefinition) {
    setCatalogLevel(nextFamily.level);
    setParameters(nextFamily.defaults);
    generateFamily(nextFamily.defaults, nextFamily);
  }

  function selectManual() {
    setFamily(null);
    setNormalization(1n);
    setFamilyRelation("exact");
    setMinimizationNotice(null);
    persistManual(coordinates);
  }

  function commitFamilyParameters() {
    if (parameters.every((value) => /^-?\d+$/.test(value))) {
      generateFamily();
      return;
    }
    setError(
      text(
        "Параметры семейства должны быть целыми числами.",
        "Family parameters must be integers.",
      ),
    );
  }

  function submitCoordinates(event: FormEvent) {
    event.preventDefault();
    try {
      const nextCoordinates: Coordinates = [
        BigInt(coordinateInputs[0].replace("−", "-")),
        BigInt(coordinateInputs[1].replace("−", "-")),
        BigInt(coordinateInputs[2].replace("−", "-")),
      ];
      setCurrentCoordinates(nextCoordinates, null);
      setNormalization(1n);
      setFamilyRelation("exact");
      persistManual(nextCoordinates);
    } catch {
      setError(
        text(
          "E, x и y должны быть целыми числами.",
          "E, x, and y must be integers.",
        ),
      );
    }
  }

  function randomize() {
    if (!family) return;
    const data = new Uint32Array(4);
    crypto.getRandomValues(data);
    const next = Array.from(data, (value) =>
      String((value % 9) + 1),
    ) as unknown as ParameterStrings;
    setParameters(next);
    generateFamily(next);
  }

  function swapPairs() {
    if (!family) return;
    const next: ParameterStrings = [
      parameters[2],
      parameters[3],
      parameters[0],
      parameters[1],
    ];
    setParameters(next);
    generateFamily(next);
  }

  function swapWithinParameters(left: number, right: number) {
    if (!family) return;
    const next = [...parameters] as [
      string,
      string,
      string,
      string,
    ];
    [next[left], next[right]] = [next[right], next[left]];
    setParameters(next);
    generateFamily(next);
  }

  function transformCoordinates(
    transform: (current: Coordinates) => Coordinates,
  ) {
    const nextCoordinates = transform(coordinates);
    setCurrentCoordinates(nextCoordinates, null);
    setNormalization(1n);
    setFamilyRelation("exact");
    persistManual(nextCoordinates);
  }

  function minimize() {
    if (!family) {
      transformCoordinates(minimizeCoordinates);
      return;
    }
    if (familyRelation === "derived") {
      minimizeFully();
      return;
    }
    minimizePreservingMask();
  }

  function minimizePreservingMask() {
    if (!family || familyRelation !== "exact") return;
    const reduction = squarePreservingReduction(coordinates);
    if (!reduction) {
      setMinimizationNotice({
        kind: "unavailable",
        dividedBy: 1n,
        remainingDivisor: greatestCommonDivisor(...coordinates),
      });
      return;
    }

    if (
      reduction.dividedBy === 1n &&
      reduction.remainingDivisor === 1n
    ) {
      setMinimizationNotice({
        kind: "primitive",
        dividedBy: 1n,
        remainingDivisor: 1n,
      });
      return;
    }

    const nextNormalization = normalization * reduction.dividedBy;
    setCurrentCoordinates(reduction.coordinates, family);
    setNormalization(nextNormalization);
    setFamilyRelation("exact");
    setMinimizationNotice({
      kind: "preserved",
      dividedBy: reduction.dividedBy,
      remainingDivisor: reduction.remainingDivisor,
    });
    persistFamily(family, parameters, nextNormalization, "exact");
  }

  function minimizeFully() {
    const divisor = greatestCommonDivisor(...coordinates);
    if (divisor < 2n) {
      setMinimizationNotice({
        kind: "primitive",
        dividedBy: 1n,
        remainingDivisor: 1n,
      });
      return;
    }

    const nextCoordinates = divideCoordinates(coordinates, divisor);
    if (!family) {
      setCurrentCoordinates(nextCoordinates, null);
      persistManual(nextCoordinates);
      return;
    }

    const nextSquarePositions = createSnapshot(nextCoordinates).squarePositions;
    const maskStillHolds = family.positions.every((position) =>
      nextSquarePositions.includes(position),
    );
    const nextRelation: FamilyRelation = maskStillHolds ? "exact" : "derived";
    const nextNormalization = normalization * divisor;
    setCurrentCoordinates(nextCoordinates, family);
    setNormalization(nextNormalization);
    setFamilyRelation(nextRelation);
    setMinimizationNotice({
      kind: nextRelation === "exact" ? "preserved" : "derived",
      dividedBy: divisor,
      remainingDivisor: 1n,
    });
    persistFamily(family, parameters, nextNormalization, nextRelation);
  }

  function multiply() {
    try {
      const factor = BigInt(scale.replace("−", "-"));
      transformCoordinates(([center, x, y]) => [
        center * factor,
        x * factor,
        y * factor,
      ]);
    } catch {
      setError(
        text(
          "Множитель должен быть целым числом.",
          "The multiplier must be an integer.",
        ),
      );
    }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  function toggleAutoPreserveMinimize(enabled: boolean) {
    setAutoPreserveMinimize(enabled);
    window.localStorage.setItem(
      "magic-squares-auto-preserve-minimize",
      String(enabled),
    );
    if (enabled && family && familyRelation === "exact") {
      minimizePreservingMask();
    }
  }

  return (
    <div className="page lab-page">
      <div className="lab-heading">
        <div>
          <p className="eyebrow">
            {text("Математическая мастерская", "Mathematical workbench")}
          </p>
          <h1>{text("Лаборатория квадратов", "Square laboratory")}</h1>
        </div>
        <p>
          {text(
            "Управляйте произвольным квадратом через E, x, y или получите эти координаты из параметрического семейства.",
            "Control an arbitrary square through E, x, and y, or obtain these coordinates from a parametric family.",
          )}
        </p>
      </div>

      <LaboratoryLayout>
        <aside className="family-panel">
          <div className="panel-label">
            <span>{text("Режим и семейства", "Mode and families")}</span>
            <small>
              {catalogFamilies.length} {text("орбиты", "orbits")}
            </small>
          </div>
          <LaboratoryLevelNavigation
            activeLevel={catalogLevel}
            onSelectLevel={(level) => {
              if (level === 6) {
                navigate(localePath(locale, "/lab/6"));
              } else {
                setCatalogLevel(level);
              }
            }}
          />
          <div className="family-list">
            <button
              className={`family-button manual-family ${family === null ? "active" : ""}`}
              onClick={selectManual}
              type="button"
            >
              <span className="manual-pattern" aria-hidden="true">
                <i>E</i>
                <i>x</i>
                <i>y</i>
              </span>
              <span>
                <strong>{text("Свободный квадрат", "Free square")}</strong>
                <small>
                  {text("прямое управление E, x, y", "direct E, x, y control")}
                </small>
              </span>
              <i>→</i>
            </button>
            {catalogFamilies.map((candidate) => (
              <button
                className={`family-button tone-${candidate.group} ${candidate.id === family?.id ? "active" : ""}`}
                onClick={() => selectFamily(candidate)}
                type="button"
                key={candidate.id}
              >
                <Pattern family={candidate} compact />
                <span>
                  <strong>{candidate.title}</strong>
                  <small>{familyGroupLabel(candidate, locale)}</small>
                </span>
                <i>→</i>
              </button>
            ))}
          </div>
        </aside>

        <LaboratoryWorkspace>
          <LaboratoryToolbar>
            <div>
              {family ? (
                <span className={`family-chip tone-${family.group}`}>
                  {familyRelation === "exact"
                    ? familyGroupLabel(family, locale)
                    : text("производный квадрат", "derived square")}
                </span>
              ) : (
                <span className="family-chip manual-chip">
                  {text("свободный режим", "free mode")}
                </span>
              )}
              <h2>{family ? family.title : "Magic3(E, x, y)"}</h2>
              {family && normalization > 1n && (
                <span className="family-normalization">
                  {text("нормировка", "normalization")} ÷
                  {formatInteger(normalization)}
                </span>
              )}
            </div>
            <button className="icon-button" type="button" onClick={copyLink}>
              {copied
                ? text("Скопировано", "Copied")
                : text("Поделиться ↗", "Share ↗")}
            </button>
          </LaboratoryToolbar>

          <LaboratoryWorkbench>
            <LaboratoryControls>
              <section className="tool-section coordinate-section">
                <div className="tool-section-heading">
                  <div>
                    <span>{text("Текущий квадрат", "Current square")}</span>
                    <small>
                      {text(
                        "три координаты определяют девять клеток",
                        "three coordinates determine all nine cells",
                      )}
                    </small>
                  </div>
                  <code>Magic3</code>
                </div>
                <form className="coordinate-form" onSubmit={submitCoordinates}>
                  <div className="coordinate-grid">
                    {(["E", "x", "y"] as const).map((name, index) => (
                      <label key={name}>
                        <span>{name}</span>
                        <input
                          aria-label={`${text("Координата", "Coordinate")} ${name}`}
                          inputMode="numeric"
                          value={coordinateInputs[index]}
                          onChange={(event) => {
                            const next = [...coordinateInputs] as [
                              string,
                              string,
                              string,
                            ];
                            next[index] = event.target.value;
                            setCoordinateInputs(next);
                          }}
                        />
                        <small>
                          {index === 0
                            ? text("центр", "center")
                            : index === 1
                              ? text("ось x", "x axis")
                              : text("ось y", "y axis")}
                        </small>
                      </label>
                    ))}
                  </div>
                  <div className="tool-actions primary-actions">
                    <button className="button button-primary" type="submit">
                      {text("Задать", "Set")}
                    </button>
                    <button
                      className="button button-quiet"
                      type="button"
                      onClick={squareFactorization.toggle}
                    >
                      {squareFactorization.factorized
                        ? text("Показать числа", "Show values")
                        : text("Факторизовать", "Factor")}
                    </button>
                    <button
                      className="button button-quiet"
                      type="button"
                      onClick={minimize}
                    >
                      {text("Минимизировать", "Minimize")}
                    </button>
                  </div>
                  {squareFactorization.factorized &&
                    squareFactorization.running && (
                    <div className="factorization-progress" role="status">
                      <span>
                        <i aria-hidden="true" />
                        {text(
                          "Факторизация выполняется в фоне",
                          "Factoring in the background",
                        )}
                      </span>
                      <button
                        className="button button-quiet"
                        type="button"
                        onClick={squareFactorization.cancel}
                      >
                        {text("Отменить", "Cancel")}
                      </button>
                    </div>
                  )}
                  {family && (
                    <label className="minimize-preference">
                      <input
                        type="checkbox"
                        checked={autoPreserveMinimize}
                        onChange={(event) =>
                          toggleAutoPreserveMinimize(event.target.checked)
                        }
                      />
                      <span>
                        <strong>
                          {text(
                            "Всегда минимизировать с сохранением маски",
                            "Always minimize while preserving the mask",
                          )}
                        </strong>
                        <small>
                          {text(
                            "автоматически для каждого нового результата семейства",
                            "automatically for every new family result",
                          )}
                        </small>
                      </span>
                    </label>
                  )}
                </form>
                {minimizationNotice && family && (
                  <div
                    className={`minimization-notice ${minimizationNotice.kind}`}
                    role="status"
                  >
                    <strong>
                      {minimizationNotice.kind === "primitive"
                        ? text(
                            "Квадрат уже примитивен",
                            "The square is already primitive",
                          )
                        : minimizationNotice.kind === "derived"
                          ? text(
                              "Выполнена полная минимизация",
                              "Full minimization applied",
                            )
                          : minimizationNotice.kind === "unavailable"
                            ? text(
                                "НОД слишком велик для быстрого разложения",
                                "The gcd is too large for quick factorization",
                              )
                            : text(
                                "Квадратная маска сохранена",
                                "The square-valued mask is preserved",
                              )}
                    </strong>
                    <p>
                      {minimizationNotice.kind === "primitive"
                        ? text(
                            "Общий делитель координат равен 1.",
                            "The coordinates have common divisor 1.",
                          )
                        : minimizationNotice.kind === "derived"
                          ? text(
                              `Координаты разделены на ${formatInteger(minimizationNotice.dividedBy)}. Квадрат остаётся связан с исходным семейством, но его квадратная маска больше не гарантируется.`,
                              `The coordinates were divided by ${formatInteger(minimizationNotice.dividedBy)}. The square remains linked to its source family, but its square-valued mask is no longer guaranteed.`,
                            )
                          : minimizationNotice.kind === "unavailable"
                            ? text(
                                "Можно выполнить полную минимизацию, но она может снять квадратность отмеченных клеток.",
                                "Full minimization is still available, but it may remove square-valuedness from the marked cells.",
                              )
                            : text(
                                `Координаты разделены на квадратный делитель ${formatInteger(minimizationNotice.dividedBy)}.`,
                                `The coordinates were divided by the square divisor ${formatInteger(minimizationNotice.dividedBy)}.`,
                              )}
                    </p>
                    {minimizationNotice.remainingDivisor > 1n && (
                      <button
                        className="button button-quiet"
                        type="button"
                        onClick={minimizeFully}
                      >
                        {text(
                          `Минимизировать полностью ÷${formatInteger(minimizationNotice.remainingDivisor)}`,
                          `Minimize fully ÷${formatInteger(minimizationNotice.remainingDivisor)}`,
                        )}
                      </button>
                    )}
                  </div>
                )}
                <div className="transform-tools">
                  <span>{text("Преобразования", "Transformations")}</span>
                  <div>
                    <button
                      type="button"
                      aria-label={text("Повернуть влево", "Rotate left")}
                      onClick={() =>
                        transformCoordinates(([e, x, y]) => [e, -y, x])
                      }
                    >
                      ↺ {text("Влево", "Left")}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        transformCoordinates(([e, x, y]) => [e, y, x])
                      }
                    >
                      ↔ {text("Отразить", "Reflect")}
                    </button>
                    <button
                      type="button"
                      aria-label={text("Повернуть вправо", "Rotate right")}
                      onClick={() =>
                        transformCoordinates(([e, x, y]) => [e, y, -x])
                      }
                    >
                      {text("Вправо", "Right")} ↻
                    </button>
                  </div>
                  <div className="multiply-tool">
                    <label>
                      <span>{text("Умножить на", "Multiply by")}</span>
                      <input
                        aria-label={text("Множитель", "Multiplier")}
                        inputMode="numeric"
                        value={scale}
                        onChange={(event) => setScale(event.target.value)}
                      />
                    </label>
                    <button type="button" onClick={multiply}>
                      {text("Применить", "Apply")}
                    </button>
                  </div>
                </div>
              </section>

              {family && (
                <section className="tool-section family-parameter-section">
                  <div className="tool-section-heading">
                    <div>
                      <div className="parameter-heading-title">
                        <span>
                          {text("Параметры", "Parameters")} {family.title}
                        </span>
                        {parameterGuide && (
                          <details className="parameter-help-popover">
                            <summary
                              aria-label={text(
                                "Пояснить параметры",
                                "Explain parameters",
                              )}
                              title={text(
                                "Пояснить параметры",
                                "Explain parameters",
                              )}
                            >
                              ?
                            </summary>
                            <div className="parameter-help-panel">
                              {parameterGuide.roles.map((role) => (
                                <section key={role.indices.join("-")}>
                                  <strong>
                                    <i
                                      className={`proof-swatch ${role.tone}`}
                                      aria-hidden="true"
                                    />
                                    {role.title}
                                  </strong>
                                  <p>{role.description}</p>
                                  {role.swapEffect && (
                                    <small>
                                      <b>↔</b> {role.swapEffect}
                                    </small>
                                  )}
                                </section>
                              ))}
                              {parameterGuide.exchange && (
                                <section>
                                  <strong>
                                    {text("Обмен пар", "Pair exchange")}
                                  </strong>
                                  <p>{parameterGuide.exchange.effect}</p>
                                </section>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                      <small>
                        {familyRelation === "derived"
                          ? text(
                              "исходные параметры; изменение восстановит семейство",
                              "source parameters; editing restores the family",
                            )
                          : normalization > 1n
                            ? text(
                                `формула применяется с нормировкой ÷${formatInteger(normalization)}`,
                                `the formula is applied with normalization ÷${formatInteger(normalization)}`,
                              )
                            : text(
                                "изменения применяются сразу",
                                "changes apply immediately",
                              )}
                      </small>
                    </div>
                    <div className="parameter-heading-actions">
                      <button
                        className="parameter-randomize"
                        type="button"
                        onClick={randomize}
                        aria-label={text(
                          "Случайные параметры",
                          "Random parameters",
                        )}
                        title={text(
                          "Случайные параметры",
                          "Random parameters",
                        )}
                      >
                        ⚄
                      </button>
                      <Pattern family={family} compact />
                    </div>
                  </div>
                  <div className="parameter-form">
                    <div className="parameter-row">
                      {PARAMETER_KEYS.map((key, index) => {
                        const role = parameterGuide?.roles.find((candidate) =>
                          candidate.indices.includes(index),
                        );
                        const symbol = parameterGuide?.symbols?.[index] ?? key;
                        return (
                          <label
                            key={key}
                            className={`parameter-input parameter-tone-${role?.tone ?? "neutral"}`}
                            title={role?.description}
                          >
                            <span>{symbol}</span>
                            <input
                              aria-label={`${text("Параметр", "Parameter")} ${symbol}`}
                              inputMode="numeric"
                              value={parameters[index]}
                              onChange={(event) => {
                                const next = [...parameters] as [
                                  string,
                                  string,
                                  string,
                                  string,
                                ];
                                next[index] = event.target.value;
                                setParameters(next);
                              }}
                              onBlur={commitFamilyParameters}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.currentTarget.blur();
                                }
                              }}
                            />
                          </label>
                        );
                      })}
                    </div>
                    {parameterGuide && (
                      <div className="parameter-swap-layout">
                        <div className="parameter-pair-swaps">
                          {parameterGuide.roles.map(
                            (role) =>
                              role.swapEffect &&
                              role.indices.length === 2 && (
                                <button
                                  className={`button parameter-swap parameter-tone-${role.tone}`}
                                  type="button"
                                  title={role.swapEffect}
                                  onClick={() =>
                                    swapWithinParameters(
                                      role.indices[0],
                                      role.indices[1],
                                    )
                                  }
                                  key={role.indices.join("-")}
                                >
                                  {parameterGuide.symbols?.[role.indices[0]] ??
                                    PARAMETER_KEYS[role.indices[0]]}
                                  {" ↔ "}
                                  {parameterGuide.symbols?.[role.indices[1]] ??
                                    PARAMETER_KEYS[role.indices[1]]}
                                </button>
                              ),
                          )}
                        </div>
                        {parameterGuide.exchange && (
                          <button
                            className="button parameter-swap parameter-pair-exchange parameter-tone-neutral"
                            type="button"
                            title={parameterGuide.exchange.effect}
                            onClick={swapPairs}
                          >
                            (
                            {parameterGuide.symbols?.[0] ?? PARAMETER_KEYS[0]},
                            {parameterGuide.symbols?.[1] ?? PARAMETER_KEYS[1]})
                            {" ↔ "}(
                            {parameterGuide.symbols?.[2] ?? PARAMETER_KEYS[2]},
                            {parameterGuide.symbols?.[3] ?? PARAMETER_KEYS[3]})
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              )}

              <section className="source-note">
                <span
                  className={`proof-icon ${family?.proofStatus === "legacy-formula" ? "legacy" : ""}`}
                >
                  {family
                    ? family.proofStatus === "proof-core"
                      ? "✓"
                      : family.proofStatus === "browser-certificate"
                        ? "✓"
                      : "∑"
                    : "E"}
                </span>
                <div>
                  <strong>
                    {family && familyRelation === "derived"
                      ? text(
                          `Производный квадрат из семейства ${family.title}`,
                          `Derived square from family ${family.title}`,
                        )
                      : family
                      ? family.proofStatus === "proof-core"
                        ? text("Символьный сертификат", "Symbolic certificate")
                        : family.proofStatus === "browser-certificate"
                          ? text(
                              "Точный алгебраический сертификат",
                              "Exact algebraic certificate",
                            )
                          : text(
                              "Legacy-формула · формализация ожидается",
                              "Legacy formula · formalization pending",
                            )
                      : text(
                          "Свободный координатный режим",
                          "Free coordinate mode",
                        )}
                  </strong>
                  <code>
                    {family
                      ? familyRelation === "derived"
                        ? `source = ${family.title}; normalization ÷${normalization}`
                        : family.theorem
                      : "square = Magic3(E, x, y)"}
                  </code>
                  <p>
                    {family && familyRelation === "derived"
                      ? text(
                          "Сохранены исходные параметры и доказательство семейства. Полная минимизация дала примитивный квадрат, но текущая маска проверяется заново и не наследуется автоматически.",
                          "The source parameters and family proof are preserved. Full minimization produced a primitive square, but the current mask is checked afresh and is not inherited automatically.",
                        )
                      : family
                      ? familySummary(family, locale)
                      : text(
                          "Специальная квадратная маска не заявляется: исследуется произвольная целочисленная тройка.",
                          "No special square-valued mask is claimed: this mode explores an arbitrary integral triple.",
                        )}
                  </p>
                  {family && (
                    <a className="source-proof-link" href="#family-proof">
                      {text("Полный текст доказательства", "Full proof text")} ↓
                    </a>
                  )}
                </div>
              </section>

              {(error || squareFactorization.error) && (
                <p className="form-error" role="alert">
                  {error || squareFactorization.error}
                </p>
              )}
            </LaboratoryControls>

            <SquareWorkbench
              coordinates={coordinates}
              snapshot={snapshot}
              declaredPositions={family?.positions}
              maskLabel={family?.mask}
              targetSquareCount={family?.level ?? 4}
              factorized={squareFactorization.factorized}
              factorizations={squareFactorization.factorizations}
            />
          </LaboratoryWorkbench>
        </LaboratoryWorkspace>
      </LaboratoryLayout>

      {family && (
        <section className="inline-family-proof" id="family-proof">
          <FamilyProofDocument family={family} />
        </section>
      )}
      {squareFactorization.warning && (
        <FactorizationWarningDialog
          warning={squareFactorization.warning}
          onCancel={squareFactorization.cancel}
          onContinue={squareFactorization.continue}
        />
      )}
    </div>
  );
}

function Pattern({ family, compact = false }: { family: FamilyDefinition; compact?: boolean }) {
  const { locale, text } = useLocale();
  const positions = "ABCDEFGHJ";
  return (
    <span
      className={`pattern ${compact ? "compact" : ""}`}
      aria-label={`${text("Доказательные опоры маски", "Proof supports for mask")} ${family.mask}`}
    >
      {Array.from(positions, (position) => {
        const supports = family.justifications.filter((item) =>
          item.positions.some((candidate) => candidate === position),
        );
        return (
          <i
            className={family.mask.includes(position) ? "on" : ""}
            key={position}
            title={
              supports
                .map((item) => justificationLabel(item, locale))
                .join(" + ") || undefined
            }
          >
            {supports.map((item) => (
              <span className={`proof-color ${item.color}`} key={item.id} />
            ))}
          </i>
        );
      })}
    </span>
  );
}

function FamilyRedirect() {
  const { familyId } = useParams();
  const family = findFamilyById(familyId);
  if (!family) return <Navigate to="/lab" replace />;
  return <Navigate to={`${orbitPath(family.id)}#family-proof`} replace />;
}

function OrbitFamilyPage() {
  const { level, familyId } = useParams();
  const family = findFamilyById(familyId);
  if (!family || String(family.level) !== level) return <NotFoundPage />;
  return <LabPage key={`${level}/${family.id}`} routeFamilyId={family.id} />;
}

function AbcdgCompleteAlgorithm() {
  const { text } = useLocale();
  return (
    <section>
      <h3>
        {text(
          "Глобально полный алгоритм ABCDG",
          "The globally complete ABCDG algorithm",
        )}
      </h3>
      <p>
        {text(
          "Полиномиальная формула предыдущего раздела строит явное бесконечное подсемейство. Для перечисления всех рациональных решений используется другое представление той же поверхности — коническое расслоение над проективной прямой.",
          "The polynomial formula in the preceding section constructs an explicit infinite subfamily. Enumerating every rational solution uses a different presentation of the same surface: a conic bundle over the projective line.",
        )}
      </p>

      <h4>{text("1. Глобальная база", "1. The global base")}</h4>
      <p>
        {text(
          "Пусть a,b,c,d,g — рациональные знаковые корни клеток A,B,C,D,G. Две клеточные связи имеют вид",
          "Let a,b,c,d,g be rational signed roots of the entries A,B,C,D,G. The two cell relations are",
        )}
      </p>
      <Latex display>
        {String.raw`b^2+c^2=d^2+g^2,\qquad 2a^2+2b^2=c^2+3g^2.`}
      </Latex>
      <p>
        {text(
          "Первая квадрика равносильна det M=0 для следующей матрицы. В проективной точке M не может быть нулевой: тогда b=c=d=g=0, а вторая квадрика даёт a=0. Поэтому M всюду имеет ранг один и единственное рациональное направление строк [r:s].",
          "The first quadric is equivalent to det M=0 for the following matrix. At a projective point M cannot vanish: that would give b=c=d=g=0, and the second quadric would then give a=0. Thus M has rank one everywhere and a unique rational row direction [r:s].",
        )}
      </p>
      <Latex display>
        {String.raw`M=\begin{pmatrix}b+d&c+g\\c-g&d-b\end{pmatrix}
=2\begin{pmatrix}rP&rQ\\sP&sQ\end{pmatrix}.`}
      </Latex>
      <p>
        {text(
          "Соответствующие координаты слоя P,Q восстанавливают все четыре корня жёлтой квадрики:",
          "The corresponding fiber coordinates P,Q recover all four roots of the yellow quadric:",
        )}
      </p>
      <Latex display>
        {String.raw`\begin{aligned}
b&=rP-sQ,&c&=sP+rQ,\\
d&=rP+sQ,&g&=rQ-sP.
\end{aligned}`}
      </Latex>
      <p>
        {text(
          "После подстановки вторая квадрика становится коникой слоя",
          "After substitution, the second quadric becomes the fiber conic",
        )}
      </p>
      <Latex display>
        {String.raw`C_{r,s}:\quad a^2=(2s^2-r^2)P^2+(2r^2-s^2)Q^2.`}
      </Latex>

      <h4>
        {text(
          "2. Точное решение каждого слоя",
          "2. Exact solution of every fiber",
        )}
      </h4>
      <p>
        {text(
          "Для примитивного представителя [r:s] оба коэффициента коники ненулевые: их зануление потребовало бы рационального числа √2. Поэтому каждый слой — гладкая проективная коника. Рациональная разрешимость тернарной квадратичной формы над Q алгоритмически разрешима. Реализация либо получает рациональную точку O, либо точное локальное препятствие; пустой слой пропускается только во втором случае.",
          "For a primitive representative [r:s], both conic coefficients are nonzero: vanishing would require a rational value of √2. Every fiber is therefore a smooth projective conic. Rational solubility of a ternary quadratic form over Q is decidable. The implementation returns either a rational point O or an exact local obstruction; a fiber is skipped only in the latter case.",
        )}
      </p>
      <p>
        {text(
          "Пусть F(X)=x²−(2s²−r²)y²−(2r²−s²)z² и B — связанная с F билинейная форма. Выберем координату j с Oⱼ≠0 и вектор U на прямой Uⱼ=0. Тогда",
          "Let F(X)=x²−(2s²−r²)y²−(2r²−s²)z² and let B be the bilinear form associated with F. Choose a coordinate j with Oⱼ≠0 and a vector U on the line Uⱼ=0. Then",
        )}
      </p>
      <Latex display>
        {String.raw`X=-F(U)O+2B(O,U)U,\qquad F(X)=F(U)^2F(O)=0.`}
      </Latex>
      <p>
        {text(
          "Точки [m:n] проективной прямой задают все направления U. Обратно, для X≠O берётся U=OⱼX−XⱼO; единственное касательное направление возвращает X=O. Следовательно, формула параметризует весь разрешимый слой, а не только открытую карту.",
          "Points [m:n] of the projective line give all directions U. Conversely, for X≠O take U=OⱼX−XⱼO; the unique tangent direction returns X=O. Hence the formula parametrizes the entire soluble fiber, not merely an open chart.",
        )}
      </p>

      <h4>
        {text(
          "3. Полный и честный перебор",
          "3. Complete fair enumeration",
        )}
      </h4>
      <p>
        {text(
          "Проективные пары перечисляются примитивными целыми представителями по высоте h([x:y])=max(|x|,|y|). Обычный вложенный цикл навсегда остался бы на первом бесконечном слое, поэтому высоты базы и параметра слоя объединяются:",
          "Projective pairs are enumerated by primitive integer representatives of height h([x:y])=max(|x|,|y|). An ordinary nested loop would remain forever on the first infinite fiber, so the base and fiber-parameter heights are dovetailed:",
        )}
      </p>
      <Latex display>
        {String.raw`h([r:s])+h([m:n])-1=H.`}
      </Latex>
      <p>
        {text(
          "На шаге H обрабатываются все пары с этой суммой высот. Поэтому каждая пара рациональных параметров и каждая рациональная точка поверхности появляются после конечного числа шагов. Результат нормализуется до примитивного знакового проективного целого вектора.",
          "At step H, every pair with this height sum is processed. Thus every pair of rational parameters and every rational point of the surface appears after finitely many steps. The result is normalized to a primitive signed projective integer vector.",
        )}
      </p>

      <h4>{text("4. Явный обратный ход", "4. Explicit inverse")}</h4>
      <p>
        {text(
          "Для заданной ненулевой точки направление базы извлекается из ненулевого столбца M:",
          "For a given nonzero point, the base direction is read from a nonzero column of M:",
        )}
      </p>
      <Latex display>
        {text(
          String.raw`[r:s]=
\begin{cases}
[b+d:c-g],&(b+d,c-g)\ne(0,0),\\
[c+g:d-b],&\text{иначе}.
\end{cases}`,
          String.raw`[r:s]=
\begin{cases}
[b+d:c-g],&(b+d,c-g)\ne(0,0),\\
[c+g:d-b],&\text{otherwise}.
\end{cases}`,
        )}
      </Latex>
      <p>
        {text(
          "После канонической нормализации [r:s] координаты слоя равны",
          "After canonical normalization of [r:s], the fiber coordinates are",
        )}
      </p>
      <Latex display>
        {String.raw`(P,Q)=
\begin{cases}
\left(\dfrac{b+d}{2r},\dfrac{c+g}{2r}\right),&r\ne0,\\[6pt]
\left(\dfrac{c-g}{2s},\dfrac{d-b}{2s}\right),&r=0.
\end{cases}`}
      </Latex>
      <p>
        {text(
          "Обратная проекция на найденной конике затем восстанавливает [m:n]. Это конечная конструкция для любой рациональной точки и потому является конструктивным доказательством сюръективности.",
          "The inverse projection on the recovered conic then reconstructs [m:n]. This finite construction applies to every rational point and is therefore a constructive proof of surjectivity.",
        )}
      </p>

      <h4>
        {text(
          "5. Переход к магическим квадратам и границы теоремы",
          "5. Magic squares and the scope of the theorem",
        )}
      </h4>
      <p>
        {text(
          "Из конечного набора знаковых подъёмов оставляется представитель с неотрицательными корнями. Остальные четыре клетки восстанавливаются линейно; при необходимости умножение корней на 2 очищает знаменатель и умножает все клетки на 4. Положительность, попарная различность и условие «квадратны ровно ABCDG» проверяются точными фильтрами. Поэтому отфильтрованный перебор достигает каждого нормального точного квадрата ABCDG и не выдаёт посторонних квадратов.",
          "Among the finitely many signed lifts, retain the representative with nonnegative roots. The other four entries are recovered linearly; when necessary, multiplying the roots by 2 clears the denominator and multiplies every entry by 4. Positivity, pairwise distinctness, and the condition that exactly ABCDG are squares are decided by exact filters. The filtered iterator therefore reaches every normal exact ABCDG square and emits no extraneous square.",
        )}
      </p>
      <p>
        {text(
          "Результат устанавливает глобальную алгоритмическую полноту. Он не даёт одной сюръективной рациональной формулы, конечного рационального атласа или оптимального перечисления по высоте.",
          "The result establishes global algorithmic completeness. It does not provide one surjective rational formula, a finite rational atlas, or a height-optimal enumeration.",
        )}
      </p>
      <ul className="proof-references">
        <li>
          <a href="https://doc.sagemath.org/html/en/reference/curves/sage/schemes/plane_conics/con_rational_field.html">
            SageMath: {text("проективные коники над Q", "projective conics over Q")}
          </a>
        </li>
        <li>
          <a href="https://pari.math.u-bordeaux.fr/dochtml/html-stable/Vectors__matrices__linear_algebra_and_sets.html">
            PARI/GP: <code>qfsolve</code> / <code>qfparam</code>
          </a>
        </li>
      </ul>
    </section>
  );
}

function FamilyProofDocument({ family }: { family: FamilyDefinition }) {
  const { locale, text } = useLocale();
  const proof = familyProof(family, locale);
  const squareStatement = String.raw`\{${family.positions.join(",")}\}\subseteq\{P:\mathcal M_P(E,x,y)=r_P^2\}`;
  const projection = String.raw`\pi_{${family.mask}}\!\left(\mathcal M(E,x,y)\right)=(${family.positions.join(",")})\in\{n^2:n\in\mathbb Z\}^{${family.level}}`;
  const cellSystem =
    proof.cellSystem ??
    String.raw`\left\{\begin{aligned}${family.positions
      .map((position) => `${CELL_FORM_LATEX[position]}&=q_${position}^2`)
      .join(String.raw`,\\`)}.\end{aligned}\right.`;
  return (
    <article className="proof-document">
      <header>
        <p>{text("Семейство", "Family")} {family.mask}</p>
        <h2>
          {text("Теорема и полное доказательство", "Theorem and complete proof")}
        </h2>
        <Link className="general-proof-link" to="/proofs/general">
          {text(
            "Общая теория орбит 4/9 и 5/9",
            "General theory of the 4/9 and 5/9 orbits",
          )}{" "}
          →
        </Link>
      </header>

      <section>
        <h3>{text("Утверждение", "Statement")}</h3>
        <p>
          {text(
            `При указанных ниже условиях формулы задают целочисленный магический квадрат порядка 3, в котором как минимум все ${family.level} клетки маски ${family.mask} являются квадратами целых чисел. Квадратность остальных клеток не запрещается.`,
            `Under the assumptions below, the formulas define an integral magic square of order 3 in which at least all ${family.level} cells of the ${family.mask} mask are squares of integers. Other cells are allowed to be squares as well.`,
          )}
        </p>
        <Latex display>{squareStatement}</Latex>
        <p>{proof.assumptions}</p>
      </section>

      <section>
        <h3>
          {text(
            "Исходная система и исключение E, x, y",
            "Initial system and elimination of E, x, y",
          )}
        </h3>
        <p>
          {text(
            "Для каждой отмеченной клетки вводим целый корень и подставляем соответствующую линейную форму Magic3. Получаем систему:",
            "For every marked cell, introduce an integral root and substitute the corresponding Magic3 linear form. This gives the system:",
          )}
        </p>
        <Latex display>{cellSystem}</Latex>
        <p>
          {text(
            `Матрица коэффициентов при E, x, y имеет ранг 3. Поэтому после их исключения остаётся ${family.level - 3} независимых однородных квадратичных ${family.level === 4 ? "уравнение" : "уравнения"} на корнях. Ниже они выводятся и одновременно параметризуются.`,
            `The coefficient matrix of E, x, and y has rank 3. Eliminating them therefore leaves ${family.level - 3} independent homogeneous quadratic ${family.level === 4 ? "equation" : "equations"} in the roots. The equations are derived and parametrized below.`,
          )}
        </p>
      </section>

      <section>
        <h3>{text("Вывод параметризации корней", "Derivation of the root parametrization")}</h3>
        <p>
          {text(
            "Введём следующие вспомогательные целые величины:",
            "Introduce the following auxiliary integers:",
          )}
        </p>
        {proof.definitions.map((formula) => (
          <Latex display key={formula}>{formula}</Latex>
        ))}
        <p>
          {text(
            "Значения заявленных клеток определим как явные квадраты:",
            "Define the declared cell values as the following explicit squares:",
          )}
        </p>
        <Latex display>{proof.squareValues}</Latex>
        <p>{proof.identityDerivation}</p>
        {proof.identities.map((identity) => (
          <Latex display key={identity}>{identity}</Latex>
        ))}
        {proof.parameterDerivation && <p>{proof.parameterDerivation}</p>}
      </section>

      <section>
        <h3>{text("Восстановление магического квадрата", "Reconstruction of the magic square")}</h3>
        <p>
          {text(
            "Используем стандартную трёхкоординатную форму:",
            "Use the standard three-coordinate form:",
          )}
        </p>
        <Latex display>{MAGIC3_LATEX}</Latex>
        <p>
          {text(
            "Положим координаты равными следующей линейной комбинации уже построенных квадратных значений:",
            "Set the coordinates equal to the following linear combination of the square values already constructed:",
          )}
        </p>
        <Latex display>{family.reconstructionLatex}</Latex>
        {proof.parityClearance && (
          <>
            <p>
              {text(
                "Если одно из делений на 2 нецелое, умножим каждый выписанный корень на 2. Тогда все значения клеток умножатся на 4, все однородные тождества сохранятся, а числители станут чётными. Именно эту нормализацию выполняет генератор.",
                "If a division by 2 is not integral, multiply every displayed root by 2. All cell values are then multiplied by 4, every homogeneous identity is preserved, and the numerators become even. This is exactly the normalization performed by the generator.",
              )}
            </p>
            <Latex display>{String.raw`q_P\mapsto2q_P,\qquad P=q_P^2\mapsto4P`}</Latex>
          </>
        )}
        {proof.integralityClearance && <p>{proof.integralityClearance}</p>}
        <p>
          {text(
            "Общая линейная лемма используется здесь прямо: если матрица выбранных клеточных форм имеет ранг 3, то вектор их значений принадлежит её образу тогда и только тогда, когда обращаются в нуль все элементы левого ядра. Для четырёх клеток левое ядро одномерно, для пяти — двумерно. Выписанные выше цветовые тождества образуют именно этот базис, а указанные формулы E, x, y дают единственный прообраз.",
            "The general linear lemma is applied directly: when the selected cell-form matrix has rank 3, its value vector lies in the image exactly when every vector in the left kernel annihilates it. The left kernel has dimension one for four cells and two for five cells. The colored identities above form precisely such a basis, while the displayed formulas for E, x, and y give the unique preimage.",
          )}
        </p>
        <Latex display>{String.raw`L_S(E,x,y)^T=(q_P^2)_{P\in S},\qquad \ker L_S^T=\langle R_1,\ldots,R_{${family.level - 3}}\rangle`}</Latex>
        <p>
          {text(
            "Теперь подставляем координаты в девять линейных форм Magic3. Поэтому",
            "Now substitute the coordinates into the nine Magic3 linear forms. Therefore",
          )}
        </p>
        <Latex display>{projection}</Latex>
        <p>
          {text(
            `Каждая строка, каждый столбец и обе диагонали имеют сумму 3E по самой форме Magic3. Следовательно, получена требуемая семья магических квадратов с квадратной маской ${family.mask}. Что и требовалось доказать.`,
            `By the Magic3 form itself, every row, every column, and both diagonals sum to 3E. We have therefore obtained the required family of magic squares with square-valued mask ${family.mask}. This proves the claim.`,
          )}
        </p>
      </section>

      <section className="proof-references">
        <h3>
          {text(
            "Цветовые леммы, применённые в этом доказательстве",
            "Color lemmas used in this proof",
          )}
        </h3>
        {family.justifications.map((item) => {
          const common = commonProofById(item.commonProofId, locale);
          if (!common) return null;
          return (
            <section className="inline-lemma" key={item.id}>
              <h4>{justificationLabel(item, locale)}</h4>
              <Latex display>{item.relationLatex}</Latex>
              <p>{common.summary}</p>
              {common.formulas.map((formula) => (
                <Latex display key={formula}>{formula}</Latex>
              ))}
              <p>
                {text(
                  `В текущей маске переменные леммы заменяются клетками ${item.positions.join(", ")}; её заключение — именно выписанное выше клеточное равенство.`,
                  `In this mask, the lemma variables are replaced by cells ${item.positions.join(", ")}; its conclusion is exactly the cell relation displayed above.`,
                )}
              </p>
              <Link to={`/proofs/${item.commonProofId}`}>
                {text(
                  "Общая формулировка и доказательство",
                  "General statement and proof",
                )}{" "}
                →
              </Link>
            </section>
          );
        })}
      </section>

      {family.id === "abcdg" && <AbcdgCompleteAlgorithm />}

      <section>
        <h3>{text("Полнота покрытия", "Coverage completeness")}</h3>
        {proof.coverage ? (
          <>
            <p>
              <strong>
                {proof.coverage.status === "complete"
                  ? text("Статус: полное покрытие.", "Status: complete coverage.")
                  : text(
                      "Статус: точное условное покрытие.",
                      "Status: exact conditional coverage.",
                    )}
              </strong>{" "}
              {text(
                "Здесь полнота относится к рациональным векторам корней; целые представители получаются очисткой знаменателей и общим масштабированием.",
                "Completeness here refers to rational root vectors; integral representatives are obtained by clearing denominators and applying a common scale.",
              )}
            </p>

            <h4>
              {text(
                "Максимально широкое гарантированное подмножество",
                "Broadest guaranteed subset",
              )}
            </h4>
            <p>{proof.coverage.guaranteedSubset}</p>
            {proof.coverage.conditions.map((condition) => (
              <Latex display key={condition}>{condition}</Latex>
            ))}

            <h4>{text("Обратный ход", "Inverse construction")}</h4>
            <p>{proof.coverage.inverseArgument}</p>

            <h4>
              {text(
                "Что остаётся вне гарантии",
                "What remains outside the guarantee",
              )}
            </h4>
            <p>{proof.coverage.exceptionalLocus}</p>
            {proof.coverage.exceptionalConditions.map((condition) => (
              <Latex display key={condition}>{condition}</Latex>
            ))}
            <p>{proof.coverage.conclusion}</p>
          </>
        ) : (
          <p>
            {proof.coverageText ??
              text(
                "Данная формула задаёт доказанное параметрическое подсемейство. Точная область обратимости ещё не зафиксирована.",
                "This formula defines a proved parametric subfamily. Its exact inverse domain has not yet been recorded.",
              )}
          </p>
        )}
      </section>

      <footer>
        <span>
          {family.proofStatus === "proof-core"
            ? text(
                "Текст согласован с универсальным полиномиальным сертификатом proof-core.",
                "This text agrees with a universal polynomial certificate in proof-core.",
              )
            : family.proofStatus === "browser-certificate"
              ? text(
                  "Формулы проверяются точным целочисленным сертификатом браузерного генератора; перенос в proof-core ещё не завершён.",
                  "The formulas are checked by an exact integral certificate in the browser generator; migration to proof-core is not complete yet.",
                )
              : text(
                  "Текст восстанавливает legacy-параметризацию; перенос машинного сертификата в proof-core ещё не завершён.",
                  "The text reconstructs a legacy parametrization; its machine certificate has not yet been migrated to proof-core.",
                )}
        </span>
        <code>{family.theorem}</code>
      </footer>
    </article>
  );
}

function atlasSourceSystem(family: FamilyDefinition): string {
  const equations = family.positions
    .map(
      (position) =>
        `${CELL_FORM_LATEX[position]}&=${position.toLowerCase()}^2`,
    )
    .join(String.raw`\\`);
  return String.raw`\left\{\begin{aligned}${equations}\end{aligned}\right.`;
}

function atlasQuadric(relation: string): string {
  return relation.replace(
    /[ABCDEFGHJ]/g,
    (position) => `${position.toLowerCase()}^2`,
  );
}

const ATLAS_POSITIONS = Array.from("ABCDEFGHJ") as Position[];

function transformAtlasPosition(
  position: Position,
  rotation: number,
  reflected: boolean,
): Position {
  const index = ATLAS_POSITIONS.indexOf(position);
  let row = Math.floor(index / 3);
  let column = index % 3;
  if (reflected) column = 2 - column;
  for (let turn = 0; turn < rotation; turn += 1) {
    [row, column] = [column, 2 - row];
  }
  return ATLAS_POSITIONS[row * 3 + column];
}

function canonicalAtlasMask(positions: readonly Position[]): string {
  const images: string[] = [];
  for (const reflected of [false, true]) {
    for (let rotation = 0; rotation < 4; rotation += 1) {
      images.push(
        positions
          .map((position) =>
            transformAtlasPosition(position, rotation, reflected),
          )
          .sort()
          .join(""),
      );
    }
  }
  return images.sort()[0];
}

const FOUR_ORBITS_BY_CANONICAL_MASK = new Map(
  FOUR_FAMILIES.map((family) => [canonicalAtlasMask(family.positions), family]),
);

function complementaryFourOrbit(
  family: FamilyDefinition,
): FamilyDefinition | undefined {
  const complement = ATLAS_POSITIONS.filter(
    (position) => !family.positions.includes(position),
  );
  return FOUR_ORBITS_BY_CANONICAL_MASK.get(canonicalAtlasMask(complement));
}

function OrbitAtlas({
  title,
  families,
}: {
  title: string;
  families: readonly FamilyDefinition[];
}) {
  const { locale, text } = useLocale();
  const legendItems = Array.from(
    new Map(
      families.flatMap((family) =>
        family.justifications.map((item) => [item.color, item] as const),
      ),
    ).values(),
  );
  return (
    <section className={`orbit-atlas orbit-atlas-${families[0]?.level ?? 4}`}>
      <header className="orbit-atlas-header">
        <div>
          <p className="eyebrow">{text("Доказательная таблица", "Proof table")}</p>
          <h4>{title}</h4>
        </div>
        <p>
          {text(
            "Цвет клетки совпадает с цветом квадрики, в которую входит её квадратное значение. Пересечение двух опор делится между двумя цветами.",
            "Each cell uses the color of the quadric containing its square value. An intersection of two supports is split between both colors.",
          )}
        </p>
        <div className="orbit-atlas-legend">
          <span>
            {text(
              "Верхний блок — исходная система; цветные полосы — квадрики:",
              "Upper block: original system; colored bars: quadrics:",
            )}
          </span>
          {legendItems.map((item) => (
            <span key={item.color}>
              <i className={`proof-swatch ${item.color}`} />
              {justificationLabel(item, locale)}
            </span>
          ))}
        </div>
      </header>
      <div className="orbit-atlas-scroll-shell">
        <div
          className="orbit-atlas-scroll"
          role="region"
          aria-label={text(
            `${title}: горизонтальная прокрутка`,
            `${title}: horizontally scrollable sheet`,
          )}
          tabIndex={0}
        >
          <div className="orbit-atlas-grid" role="list" aria-label={title}>
            {families.map((family, index) => {
              const complement =
                family.level === 5 ? complementaryFourOrbit(family) : undefined;
              const description = complement
                ? text(
                    `Дополнение к типу 4/9 «${familyOrbitDescription(complement, locale)}»`,
                    `Complement of the 4/9 type “${familyOrbitDescription(complement, locale)}”`,
                  )
                : familyOrbitDescription(family, locale) ??
                  familyGroupLabel(family, locale);
              return (
                <article
                  className={`orbit-atlas-entry tone-${family.group}`}
                  role="listitem"
                  key={family.id}
                >
                  <header className="orbit-atlas-entry-header">
                    <span className="orbit-atlas-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <Pattern family={family} />
                    <div>
                      <Link
                        className="orbit-atlas-title"
                        to={`${orbitPath(family.id)}#family-proof`}
                      >
                        {family.title} →
                      </Link>
                      <p>{description}</p>
                    </div>
                  </header>

                  <div className="orbit-atlas-system">
                    <Latex display>{atlasSourceSystem(family)}</Latex>
                  </div>

                  <div className="orbit-atlas-quadrics">
                    {family.justifications.map((item) => {
                      const label = justificationLabel(item, locale);
                      return (
                        <div
                          aria-label={label}
                          className={`orbit-atlas-relation ${item.color}`}
                          key={item.id}
                          title={label}
                        >
                          <Latex display>{atlasQuadric(item.relationLatex)}</Latex>
                        </div>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function SquaresOfSquaresPage() {
  const { text } = useLocale();
  return (
    <article className="page proof-page topic-page">
      <header className="proof-page-header">
        <div>
          <p className="eyebrow">
            {text(
              "Открытая задача и её частичные формы",
              "The open problem and its partial forms",
            )}
          </p>
          <h1>
            {text(
              "Магический квадрат из квадратов 3×3",
              "The 3×3 magic square of squares",
            )}
          </h1>
          <p>
            {text(
              "Существует ли невырожденный магический квадрат порядка 3, все девять различных целых значений которого являются полными квадратами? Для обычной арифметики эта задача остаётся открытой.",
              "Does there exist a nondegenerate order-3 magic square whose nine distinct integer entries are all perfect squares? Over the ordinary integers, this problem remains open.",
            )}
          </p>
        </div>
      </header>

      <div className="proof-document topic-document">
        <section>
          <h2>{text("Общая форма", "The general form")}</h2>
          <p>
            {text(
              "Каждый магический квадрат 3×3 однозначно задаётся тремя координатами E, x, y. Центр равен E, а магическая сумма равна 3E:",
              "Every 3×3 magic square is uniquely determined by three coordinates E, x, and y. Its center is E and its magic sum is 3E:",
            )}
          </p>
          <Latex display>{MAGIC3_LATEX}</Latex>
          <p>
            {text(
              "Полная задача 9/9 требует, чтобы все девять линейных форм были различными неотрицательными квадратами целых чисел. Лаборатория позволяет непосредственно изменять E, x, y и проверять получающийся квадрат.",
              "The full 9/9 problem requires all nine linear forms to be distinct nonnegative integer squares. The laboratory exposes E, x, and y directly and checks the resulting square.",
            )}
          </p>
          <div className="topic-actions">
            <Link className="button button-primary" to="/lab">
              {text("Открыть калькулятор", "Open the calculator")} <span>↗</span>
            </Link>
            <Link className="button button-ghost" to="/theory/magic-squares-3x3">
              {text(
                "Доказательство общей формы",
                "Proof of the general form",
              )}
            </Link>
          </div>
        </section>

        <section>
          <h2>
            {text(
              "Почему мы начинаем с 4/9 и 5/9",
              "Why the atlas starts with 4/9 and 5/9",
            )}
          </h2>
          <p>
            {text(
              "Вместо недоказанного существования 9/9 мы фиксируем набор клеток, значения которых гарантированно являются квадратами. Это условие не запрещает остальным клеткам случайно оказаться квадратными: 4/9 и 5/9 обозначают гарантированную маску, а не обязательно точное число квадратных значений.",
              "Instead of assuming an unproved 9/9 solution, we fix a set of cells whose values are guaranteed to be squares. Other cells may also happen to be square-valued: 4/9 and 5/9 describe a guaranteed mask, not necessarily the exact number of square entries.",
            )}
          </p>
          <p>
            {text(
              "Вращения и отражения квадрата образуют группу D₄. С точностью до этого действия существует 23 маски из четырёх клеток и 23 дополнительные к ним маски из пяти клеток.",
              "Rotations and reflections form the group D4. Modulo this action there are 23 four-cell masks and 23 complementary five-cell masks.",
            )}
          </p>
          <div className="topic-route-grid">
            <Link to="/orbits/4">
              <span>4/9</span>
              <strong>{text("23 орбиты четырёх клеток", "23 four-cell orbits")}</strong>
              <i>→</i>
            </Link>
            <Link to="/orbits/5">
              <span>5/9</span>
              <strong>{text("23 орбиты пяти клеток", "23 five-cell orbits")}</strong>
              <i>→</i>
            </Link>
            <Link to="/orbits/6">
              <span>6/9</span>
              <strong>{text("16 орбит шести клеток", "16 six-cell orbits")}</strong>
              <i>→</i>
            </Link>
            <Link to="/orbits/7">
              <span>7/9</span>
              <strong>{text("8 трёхугловых орбит", "8 three-angle orbits")}</strong>
              <i>→</i>
            </Link>
          </div>
        </section>

        <section>
          <h2>{text("Что содержит доказательство", "What the proof contains")}</h2>
          <ul>
            <li>
              {text(
                "полное разложение масок по орбитам D₄ и доказательство отсутствия других орбит;",
                "a complete decomposition into D4 orbits and a proof that no further orbits exist;",
              )}
            </li>
            <li>
              {text(
                "исходную систему уравнений в E, x, y и квадратных корнях;",
                "the original equations in E, x, y, and the square roots;",
              )}
            </li>
            <li>
              {text(
                "исключение E, x, y до одной квадрики для 4/9 и двух независимых квадрик для 5/9;",
                "elimination of E, x, and y to one quadric for 4/9 and two independent quadrics for 5/9;",
              )}
            </li>
            <li>
              {text(
                "явные параметризации, условия применимости и максимально широкие доказанные области покрытия.",
                "explicit parametrizations, applicability assumptions, and the widest proved coverage domains currently available.",
              )}
            </li>
          </ul>
        </section>
      </div>
    </article>
  );
}

function BasicMagicTheoryPage() {
  const { text } = useLocale();
  return (
    <article className="page proof-page topic-page basic-theory-page">
      <Link className="back-link" to="/theory">
        ← {text("К оглавлению теории", "Back to theory contents")}
      </Link>
      <header className="proof-page-header">
        <div>
          <p className="eyebrow">
            {text("Базовая теория", "Basic theory")}
          </p>
          <h1>
            {text(
              "Магические квадраты 3×3 над произвольным носителем",
              "3×3 magic squares over a general carrier",
            )}
          </h1>
          <p>
            {text(
              "Магическая константа, полная форма m(E,x,y), её единственность и точные различия между абелевыми группами, кольцами, полями, вещественными, рациональными и целыми числами.",
              "The magic constant, the complete form m(E,x,y), its uniqueness, and the exact distinctions between abelian groups, rings, fields, and the real, rational, and integer cases.",
            )}
          </p>
        </div>
      </header>

      <div className="proof-document topic-document basic-theory-document">
        <section>
          <h2>{text("1. Носитель и определение", "1. Carrier and definition")}</h2>
          <p>
            {text(
              "Пусть A — абелева группа в аддитивной записи. Магическим квадратом 3×3 над A назовём таблицу, у которой суммы трёх строк, трёх столбцов и двух главных диагоналей равны одному элементу M∈A. Абелева группа — естественная общая граница теории: в доказательстве нужны сложение, нуль и вычитание, но не нужны умножение, порядок или деление.",
              "Let A be an abelian group written additively. A 3×3 magic square over A is an array whose three row sums, three column sums, and two main diagonal sums are equal to one element M∈A. An abelian group is the natural general setting: the proof needs addition, zero, and subtraction, but it needs no multiplication, order, or division.",
            )}
          </p>
          <Latex display>{String.raw`Q=\begin{pmatrix}A&B&C\\D&E&F\\G&H&J\end{pmatrix},\qquad
A+B+C=\cdots=C+E+G=M.`}</Latex>
          <p>
            {text(
              "Если A является модулем над кольцом R, то сложение квадратов и умножение всех клеток на один скаляр выполняются поклеточно. Полная классификация ниже совместима с этой модульной структурой.",
              "If A is a module over a ring R, squares are added entrywise and every entry may be multiplied by one scalar. The classification below is compatible with this module structure.",
            )}
          </p>
        </section>

        <section>
          <h2>{text("2. Магическая константа", "2. The magic constant")}</h2>
          <div className="theorem-block">
            <h3>{text("Теорема", "Theorem")}</h3>
            <p>
              {text(
                "В любом магическом квадрате 3×3 над абелевой группой магическая константа равна утроенному центральному элементу:",
                "In every 3×3 magic square over an abelian group, the magic constant is three times the central entry:",
              )}
            </p>
            <Latex display>{String.raw`M=3E.`}</Latex>
          </div>
          <h3>{text("Доказательство", "Proof")}</h3>
          <p>
            {text(
              "Обозначим сумму всех девяти клеток через T. Сложение трёх строк даёт T=3M. Теперь сложим среднюю строку, средний столбец и две диагонали. Каждая нецентральная клетка встретится один раз, а центр — четыре раза. Поэтому эта сумма одновременно равна 4M и T+3E:",
              "Let T be the sum of all nine entries. Adding the three rows gives T=3M. Now add the middle row, the middle column, and both diagonals. Every noncentral entry occurs once and the center occurs four times. Hence this sum is both 4M and T+3E:",
            )}
          </p>
          <Latex display>{String.raw`T=3M,\qquad
(D+E+F)+(B+E+H)+(A+E+J)+(C+E+G)=T+3E=4M.`}</Latex>
          <p>
            {text(
              "Подставляя T=3M и сокращая 3M в абелевой группе, получаем M=3E. Деление на 3 здесь не использовалось. Из четырёх линий, проходящих через центр, сразу следует дополнительное равенство противоположных пар:",
              "Substituting T=3M and cancelling 3M in the abelian group gives M=3E. No division by 3 was used. The four lines through the center immediately give the opposite-pair identities:",
            )}
          </p>
          <Latex display>{String.raw`A+J=B+H=C+G=D+F=2E.`}</Latex>
        </section>

        <section>
          <h2>{text("3. Полная форма m(E,x,y)", "3. The complete form m(E,x,y)")}</h2>
          <div className="theorem-block">
            <h3>{text("Теорема о классификации", "Classification theorem")}</h3>
            <p>
              {text(
                "Для любой абелевой группы A каждый магический квадрат 3×3 над A единственным образом представляется в следующем виде, где E,x,y∈A:",
                "For every abelian group A, each 3×3 magic square over A has a unique representation of the following form, with E,x,y∈A:",
              )}
            </p>
            <Latex display>{MAGIC3_LATEX}</Latex>
          </div>
          <h3>
            {text(
              "Сюръективность: каждый магический квадрат имеет эту форму",
              "Surjectivity: every magic square has this form",
            )}
          </h3>
          <p>
            {text(
              "Пусть дан произвольный магический квадрат Q. Положим x=A−E и y=G−E. Тогда A=E+x и G=E+y. Из равенств противоположных пар получаем J=E−x и C=E−y. Нижняя строка и первая колонка затем дают H=E+x−y и D=E−x−y, а противоположные им клетки равны B=E−x+y и F=E+x+y. Тем самым все девять клеток имеют заявленный вид.",
              "Let Q be any magic square and set x=A−E and y=G−E. Then A=E+x and G=E+y. The opposite-pair identities give J=E−x and C=E−y. The bottom row and first column then give H=E+x−y and D=E−x−y, while their opposite entries are B=E−x+y and F=E+x+y. Thus all nine entries have the stated form.",
            )}
          </p>
          <Latex display>{String.raw`\begin{aligned}
x&=A-E,& y&=G-E,\\
A&=E+x,& G&=E+y,\\
J&=2E-A=E-x,& C&=2E-G=E-y,\\
H&=3E-G-J=E+x-y,& B&=2E-H=E-x+y,\\
D&=3E-A-G=E-x-y,& F&=2E-D=E+x+y.
\end{aligned}`}</Latex>
          <p>
            {text(
              "Следовательно, для каждого Q∈𝓜₃(A) найден прообраз (E,x,y), то есть отображение Φₐ:A³→𝓜₃(A) сюръективно.",
              "Consequently every Q∈𝓜₃(A) has a preimage (E,x,y), so the map Φₐ:A³→𝓜₃(A) is surjective.",
            )}
          </p>
          <h3>
            {text(
              "Корректность: каждый набор координат даёт магический квадрат",
              "Well-definedness: every coordinate triple gives a magic square",
            )}
          </h3>
          <p>
            {text(
              "Для любых E,x,y восемь требуемых сумм вычисляются непосредственно. Ни одно из равенств не использует деление:",
              "For arbitrary E,x,y, the eight required sums are computed directly. None of these identities uses division:",
            )}
          </p>
          <Latex display>{String.raw`\begin{aligned}
(E+x)+(E-x+y)+(E-y)&=3E,\\
(E-x-y)+E+(E+x+y)&=3E,\\
(E+y)+(E+x-y)+(E-x)&=3E,\\
(E+x)+(E-x-y)+(E+y)&=3E,\\
(E-x+y)+E+(E+x-y)&=3E,\\
(E-y)+(E+x+y)+(E-x)&=3E,\\
(E+x)+E+(E-x)&=3E,\\
(E-y)+E+(E+y)&=3E.
\end{aligned}`}</Latex>
          <h3>
            {text(
              "Инъективность: координаты единственны",
              "Injectivity: the coordinates are unique",
            )}
          </h3>
          <p>
            {text(
              "Координаты восстанавливаются без выбора и без знаменателей: E — центральная клетка, x=A−E, y=G−E. Поэтому два различных набора координат не могут задавать один квадрат.",
              "The coordinates are recovered without choices or denominators: E is the central entry, x=A−E, and y=G−E. Therefore two different coordinate triples cannot define the same square.",
            )}
          </p>
          <Latex display>{String.raw`(E,x,y)=\bigl(Q_{22},\ Q_{11}-Q_{22},\ Q_{31}-Q_{22}\bigr).`}</Latex>
        </section>

        <section>
          <h2>{text("4. Группа, модуль и векторное пространство", "4. Group, module, and vector space")}</h2>
          <p>
            {text(
              "Обозначим множество магических квадратов 3×3 над A через 𝓜₃(A). Теорема задаёт изоморфизм абелевых групп A³≅𝓜₃(A). Если A — R-модуль, этот изоморфизм R-линеен:",
              "Write 𝓜₃(A) for the set of 3×3 magic squares over A. The theorem gives an isomorphism of abelian groups A³≅𝓜₃(A). If A is an R-module, this isomorphism is R-linear:",
            )}
          </p>
          <Latex display>{String.raw`\begin{aligned}
\Phi_A:A^3&\longrightarrow\mathcal M_3(A),&(E,x,y)&\longmapsto\mathcal M(E,x,y),\\
\mathcal M(E_1,x_1,y_1)+\mathcal M(E_2,x_2,y_2)
&=\mathcal M(E_1+E_2,x_1+x_2,y_1+y_2),\\
r\mathcal M(E,x,y)&=\mathcal M(rE,rx,ry).
\end{aligned}`}</Latex>
          <p>
            {text(
              "Для коммутативного кольца R это свободный R-модуль с тремя явными базисными матрицами:",
              "For a commutative ring R, this is a free R-module with the following three explicit basis matrices:",
            )}
          </p>
          <Latex display>{String.raw`\mathcal M(E,x,y)=E S_0+xS_1+yS_2,`}</Latex>
          <Latex display>{String.raw`S_0=\begin{pmatrix}1&1&1\\1&1&1\\1&1&1\end{pmatrix},\quad
S_1=\begin{pmatrix}1&-1&0\\-1&0&1\\0&1&-1\end{pmatrix},\quad
S_2=\begin{pmatrix}0&1&-1\\-1&0&1\\1&-1&0\end{pmatrix}.`}</Latex>
          <p>
            {text(
              "В частности, над любым полем K пространство 𝓜₃(K) трёхмерно. Это утверждение относится к поклеточному сложению и умножению на скаляр; обычное матричное умножение магические квадраты в общем случае не сохраняет.",
              "In particular, over every field K the space 𝓜₃(K) has dimension three. This statement concerns entrywise addition and scalar multiplication; ordinary matrix multiplication does not in general preserve magic squares.",
            )}
          </p>
        </section>

        <section>
          <h2>{text("5. Различные области коэффициентов", "5. Different scalar domains")}</h2>
          <div className="domain-table-wrap">
            <table className="domain-table">
              <thead>
                <tr>
                  <th>{text("Носитель", "Carrier")}</th>
                  <th>{text("Структура", "Structure")}</th>
                  <th>{text("Точное следствие", "Exact consequence")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><Latex>{String.raw`A`}</Latex></td>
                  <td>{text("абелева группа", "abelian group")}</td>
                  <td><Latex>{String.raw`\mathcal M_3(A)\cong A^3`}</Latex></td>
                </tr>
                <tr>
                  <td><Latex>{String.raw`R`}</Latex></td>
                  <td>{text("коммутативное кольцо", "commutative ring")}</td>
                  <td>{text("свободный R-модуль ранга 3", "free R-module of rank 3")}</td>
                </tr>
                <tr>
                  <td><Latex>{String.raw`K`}</Latex></td>
                  <td>{text("поле", "field")}</td>
                  <td><Latex>{String.raw`\dim_K\mathcal M_3(K)=3`}</Latex></td>
                </tr>
                <tr>
                  <td><Latex>{String.raw`\mathbb R`}</Latex></td>
                  <td>{text("упорядоченное поле", "ordered field")}</td>
                  <td>{text("фиксированное M задаёт аффинную плоскость E=M/3", "fixed M gives the affine plane E=M/3")}</td>
                </tr>
                <tr>
                  <td><Latex>{String.raw`\mathbb Q`}</Latex></td>
                  <td>{text("рациональное поле", "rational field")}</td>
                  <td>{text("общий знаменатель переводит квадрат в целочисленный", "a common denominator scales the square to an integral one")}</td>
                </tr>
                <tr>
                  <td><Latex>{String.raw`\mathbb Z`}</Latex></td>
                  <td>{text("свободная абелева группа", "free abelian group")}</td>
                  <td><Latex>{String.raw`\mathcal M_3(\mathbb Z)\cong\mathbb Z^3`}</Latex></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>{text("Вещественные числа", "Real numbers")}</h3>
          <p>
            {text(
              "Над ℝ получаем трёхмерное вещественное векторное пространство. При фиксированной магической константе M координата E=M/3 фиксирована, а x и y свободны, поэтому получается двумерная аффинная плоскость. Положительность клеток задаётся девятью линейными неравенствами, а попарная различность — исключением конечного набора прямых в плоскости (x,y).",
              "Over ℝ one obtains a three-dimensional real vector space. For a fixed magic constant M, the coordinate E=M/3 is fixed while x and y are free, giving a two-dimensional affine plane. Positivity is expressed by nine linear inequalities, while pairwise distinctness removes a finite collection of lines from the (x,y)-plane.",
            )}
          </p>

          <h3>{text("Рациональные числа", "Rational numbers")}</h3>
          <p>
            {text(
              "Рациональные клетки эквивалентны рациональным координатам E,x,y. Умножение на общий знаменатель координат переводит любой рациональный магический квадрат в целочисленный. Если требуется сохранить представление клеток именно рациональными квадратами, берут общий знаменатель их квадратных корней и умножают весь квадрат на квадрат этого знаменателя.",
              "Rational entries are equivalent to rational coordinates E,x,y. Multiplying by a common denominator of the coordinates turns any rational magic square into an integral one. To preserve the stronger property that the entries are rational squares, take a common denominator of their square roots and multiply the whole square by the square of that denominator.",
            )}
          </p>

          <h3>{text("Целые числа", "Integers")}</h3>
          <p>
            {text(
              "Целочисленный магический квадрат имеет целые E,x,y, поскольку эти координаты являются целочисленными разностями его клеток; обратное очевидно из формулы. Магическая константа обязательно делится на 3. Кроме того, примитивность полностью читается по координатам:",
              "An integral magic square has integral E,x,y because these coordinates are integral differences of its entries; the converse is immediate from the formula. Its magic constant is necessarily divisible by 3. Primitivity is also read directly from the coordinates:",
            )}
          </p>
          <Latex display>{text(
            String.raw`\gcd\{\text{девять клеток}\}=\gcd(E,x,y),`,
            String.raw`\gcd\{\text{nine entries}\}=\gcd(E,x,y).`,
          )}</Latex>
          <p>
            {text(
              "Действительно, каждая клетка — целочисленная линейная комбинация E,x,y, а E,x,y, в свою очередь, являются целочисленными линейными комбинациями клеток. Поэтому два НОД делят друг друга.",
              "Indeed, every entry is an integral linear combination of E,x,y, while E,x,y are themselves integral linear combinations of the entries. Hence the two gcds divide each other.",
            )}
          </p>

          <h3>{text("Поля положительной характеристики", "Fields of positive characteristic")}</h3>
          <p>
            {text(
              "Форма m(E,x,y) и трёхмерность сохраняются в любой характеристике, но привычная геометрия может вырождаться. В характеристике 3 каждый магический квадрат имеет M=3E=0, поэтому M уже не определяет центр. В характеристике 2 противоположные клетки совпадают, а четыре боковые клетки B,D,F,H имеют одно значение; квадрат не может иметь девять попарно различных клеток:",
              "The form m(E,x,y) and the three-dimensional classification remain valid in every characteristic, but the familiar geometry may degenerate. In characteristic 3 every magic square has M=3E=0, so M no longer determines the center. In characteristic 2 opposite entries coincide and the four side entries B,D,F,H share one value; the square cannot have nine pairwise distinct entries:",
            )}
          </p>
          <Latex display>{String.raw`\operatorname{char}K=2:\qquad A=J,\quad C=G,\quad B=D=F=H.`}</Latex>
          <p>
            {text(
              "Над кольцом, где 2 или 3 не обратимы, безопасно пользоваться беззнаменательной формой и восстановлением E=Q₂₂, x=A−E, y=G−E. Формулы E=M/3, x=(A−J)/2 и y=(G−C)/2 допустимы только при обратимости соответствующих элементов.",
              "Over a ring in which 2 or 3 is not invertible, one should use the denominator-free form and recover E=Q₂₂, x=A−E, y=G−E. The formulas E=M/3, x=(A−J)/2, and y=(G−C)/2 are valid only when the corresponding elements are invertible.",
            )}
          </p>
        </section>

        <section>
          <h2>{text("6. Граница линейной теории", "6. Boundary of the linear theory")}</h2>
          <p>
            {text(
              "Теорема классифицирует все магические квадраты 3×3, но сама по себе ничего не утверждает о положительности, различности или квадратности клеток. Положительность требует порядка; понятие полного квадрата требует умножения; одновременная квадратность выбранных линейных форм превращает линейную задачу в диофантову. Именно с этой точки начинаются задачи k/9, теория вычетов, нормы и эллиптические поверхности.",
              "The theorem classifies all 3×3 magic squares, but by itself says nothing about positivity, distinctness, or square-valued entries. Positivity requires an order; being a square requires multiplication; requiring selected linear forms to be squares turns the linear problem into a Diophantine one. This is the point at which the k/9 problems, residue theory, norms, and elliptic surfaces begin.",
            )}
          </p>
          <div className="topic-actions">
            <Link className="button button-primary" to="/lab">
              {text("Открыть m(E,x,y) в калькуляторе", "Open m(E,x,y) in the calculator")} <span>↗</span>
            </Link>
            <Link className="button button-ghost" to="/proofs/general">
              {text("Перейти к теории 4/9 и 5/9", "Continue to the 4/9 and 5/9 theory")}
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}

function OrbitLevelPage({ level }: { level: FamilyLevel }) {
  const { text } = useLocale();
  const families = level === 4 ? FOUR_FAMILIES : FIVE_FAMILIES;
  return (
    <article className="page proof-page orbit-level-page">
      <Link className="back-link" to="/squares-of-squares">
        ← {text("К постановке задачи", "Back to the problem")}
      </Link>
      <header className="proof-page-header">
        <div>
          <p className="eyebrow">
            {text("Полная классификация по D₄", "Complete D4 classification")}
          </p>
          <h1>
            {level === 4
              ? text(
                  "Четыре квадратные клетки: 23 орбиты",
                  "Four square entries: 23 orbits",
                )
              : text(
                  "Пять квадратных клеток: 23 орбиты",
                  "Five square entries: 23 orbits",
                )}
          </h1>
          <p>
            {level === 4
              ? text(
                  "Каждая маска задаёт четыре гарантированно квадратных значения и одну квадрику совместимости после исключения E, x, y.",
                  "Each mask specifies four guaranteed square values and one compatibility quadric after E, x, and y are eliminated.",
                )
              : text(
                  "Каждая маска задаёт пять гарантированно квадратных значений и две независимые квадрики совместимости после исключения E, x, y.",
                  "Each mask specifies five guaranteed square values and two independent compatibility quadrics after E, x, and y are eliminated.",
                )}
          </p>
        </div>
      </header>

      <div className="proof-document orbit-level-document">
        <section>
          <p>
            {text(
              "Число в названии — нижняя гарантия, а не запрет на дополнительные квадратные клетки. Нажмите название маски, чтобы открыть ту же лабораторию с её параметрами и полным доказательством непосредственно под квадратом.",
              "The level is a lower guarantee, not a prohibition on additional square-valued cells. Open a mask to load the same laboratory with its parameters and complete proof directly below the square.",
            )}
          </p>
          <p>
            <Link className="general-proof-link" to="/proofs/general">
              {text(
                "Доказательство полноты классификации и достаточности квадрик",
                "Proof of classification completeness and sufficiency of the quadrics",
              )}{" "}
              →
            </Link>
          </p>
          <OrbitAtlas
            title={
              level === 4
                ? text(
                    "Все 23 орбиты и квадрики 4/9",
                    "All 23 orbits and quadrics for 4/9",
                  )
                : text(
                    "Все 23 орбиты и пары квадрик 5/9",
                    "All 23 orbits and pairs of quadrics for 5/9",
                  )
            }
            families={families}
          />
        </section>
      </div>
    </article>
  );
}

function GeneralTheoryPage() {
  const { locale, text } = useLocale();
  const proofs = commonProofs(locale);
  return (
    <article className="page proof-page general-theory-page">
      <Link className="back-link" to="/theory#partial-configurations">
        ← {text("К циклу о частичных конфигурациях", "Back to the partial-configurations series")}
      </Link>
      <header className="proof-page-header">
        <div>
          <p className="eyebrow">
            {text(
              "Частичные квадратные конфигурации · 3.1",
              "Partial square configurations · 3.1",
            )}
          </p>
          <h1>{text("Орбиты и квадрики 4/9 → 5/9", "Orbits and quadrics: 4/9 → 5/9")}</h1>
          <p>
            {text(
              "Классификация масок, исключение E, x, y, доказательство достаточности цветовых уравнений и общий механизм параметризации.",
              "Classification of masks, elimination of E, x, and y, sufficiency of the colored equations, and the common parametrization mechanism.",
            )}
          </p>
        </div>
      </header>

      <div className="proof-document general-proof-document">
        <section>
          <h3>{text("1. Координатная модель", "1. Coordinate model")}</h3>
          <p>
            {text(
              "Любой обычный магический квадрат порядка 3 над коммутативным кольцом записывается в трёх координатах E, x, y:",
              "Every ordinary magic square of order 3 over a commutative ring can be written in the three coordinates E, x, and y:",
            )}
          </p>
          <Latex display>{MAGIC3_LATEX}</Latex>
          <p>
            {text(
              "Одновременно девять клеточных форм образуют матрицу",
              "Equivalently, the nine cell forms make up the matrix equation",
            )}
          </p>
          <Latex display>{String.raw`\begin{pmatrix}A\\B\\C\\D\\E\\F\\G\\H\\J\end{pmatrix}=L\begin{pmatrix}E\\x\\y\end{pmatrix},\qquad L=\begin{pmatrix}1&1&0\\1&-1&1\\1&0&-1\\1&-1&-1\\1&0&0\\1&1&1\\1&0&1\\1&1&-1\\1&-1&0\end{pmatrix}`}</Latex>
          <p>
            {text(
              "Для маски S вводим независимые целые корни qₚ и записываем исходную систему без сокращений:",
              "For a mask S, introduce independent integral roots qₚ and write the original system without abbreviation:",
            )}
          </p>
          <Latex display>{String.raw`L_S\begin{pmatrix}E\\x\\y\end{pmatrix}=q_S^{[2]},\qquad q_S^{[2]}=(q_P^2)_{P\in S}`}</Latex>
          <p>
            {text(
              "Здесь утверждается квадратность всех клеток S, но не неквадратность дополнения. Поэтому термин k/9 далее означает «как минимум эти k клеток являются квадратами».",
              "This asserts that every cell in S is a square, but does not assert that cells outside S are nonsquares. Thus k/9 means “at least these k cells are perfect squares.”",
            )}
          </p>
        </section>

        <section>
          <h3>
            {text(
              "2. Теорема исключения и достаточности",
              "2. Elimination and sufficiency theorem",
            )}
          </h3>
          <p>
            {text(
              "Коэффициентные пары (x,y) девяти клеток образуют решётку 3×3. Четыре различные точки этой решётки не лежат на одной прямой; следовательно, для любой маски из четырёх или пяти клеток матрица Lₛ имеет ранг 3.",
              "The (x,y) coefficient pairs of the nine cells form a 3×3 grid. No four distinct points of this grid are collinear; therefore Lₛ has rank 3 for every four- or five-cell mask.",
            )}
          </p>
          <Latex display>{String.raw`\dim\ker L_S^T=|S|-3=\begin{cases}1,&|S|=4,\\2,&|S|=5.\end{cases}`}</Latex>
          <p>
            {text(
              "Пусть R₁,…,R|S|−3 — базис левого ядра. Необходимость уравнений Rᵢ(q²)=0 получается умножением исходной системы слева. Обратно, над Q ортогональное дополнение левого ядра совпадает с образом Lₛ. Поэтому эти уравнения достаточны и дают единственную тройку E, x, y.",
              "Let R₁,…,R|S|−3 be a basis of the left kernel. Multiplying the original system on the left proves the necessity of Rᵢ(q²)=0. Conversely, over Q the orthogonal complement of the left kernel equals the image of Lₛ. Hence these equations are sufficient and determine a unique triple E, x, y.",
            )}
          </p>
          <Latex display>{String.raw`q_S^{[2]}\in\operatorname{im}L_S\quad\Longleftrightarrow\quad R_i(q_S^{[2]})=0\quad(1\le i\le |S|-3)`}</Latex>
          <p>
            {text(
              "Для целочисленности выбираем ненулевой минор δ порядка 3. Формулы Крамера имеют знаменатель δ. Замена каждого корня qₚ на δqₚ умножает правую часть на δ² и превращает координаты в целые:",
              "To obtain integral coordinates, choose a nonzero 3×3 minor δ. Cramer's formulas have denominator δ. Replacing every root qₚ by δqₚ multiplies the right-hand side by δ² and makes the coordinates integral:",
            )}
          </p>
          <Latex display>{String.raw`q_P\mapsto\delta q_P,\qquad q_P^2\mapsto\delta^2q_P^2,\qquad (E,x,y)\mapsto\delta^2(E,x,y)\in\mathbb Z^3`}</Latex>
        </section>

        <section>
          <h3>{text("3. Почему орбит ровно 23", "3. Why there are exactly 23 orbits")}</h3>
          <p>
            {text(
              "Группа D₄ действует вращениями и отражениями, сохраняя центр, множество четырёх углов и множество четырёх сторон. По лемме Бёрнсайда числа неподвижных пятиэлементных масок равны 126 для тождества, 2 для каждого поворота на ±90°, 6 для поворота на 180° и 12 для каждого из четырёх отражений. Поэтому",
              "The group D₄ acts by rotations and reflections, preserving the center, the set of four corners, and the set of four edge cells. By Burnside's lemma, the numbers of fixed five-cell masks are 126 for the identity, 2 for each ±90° rotation, 6 for the 180° rotation, and 12 for each of the four reflections. Therefore",
            )}
          </p>
          <Latex display>{String.raw`N_{5/9}=\frac{126+2+6+2+4\cdot12}{8}=23`}</Latex>
          <p>
            {text(
              "Тот же результат получается топологически для четырёх клеток. Без центра выбираются четыре клетки периметра: случаи 0 или 4 угла дают 2 орбиты, случаи 1 или 3 угла — 4, а случай 2+2 распадается на 7; итого 13. С центром выбираются три клетки периметра: крайние случаи дают 2, а типы 1+2 и 2+1 — по 4; итого 10. Сумма 13+10=23.",
              "The same result follows from the corner/edge topology for four cells. Without the center, choose four perimeter cells: 0 or 4 corners give 2 orbits, 1 or 3 corners give 4, and the 2+2 case splits into 7, for a total of 13. With the center, choose three perimeter cells: the extreme cases give 2, while the 1+2 and 2+1 types give 4 each, for a total of 10. Thus 13+10=23.",
            )}
          </p>
          <p>
            {text(
              "Дополнение маски коммутирует с D₄ и задаёт биекцию между орбитами 4/9 и 5/9. Именно здесь исходный PDF пропустил ACDH, а вслед за ним дополнительную маску BEFGJ.",
              "Taking the complement of a mask commutes with D₄ and gives a bijection between the 4/9 and 5/9 orbits. The original PDF omitted ACDH and, consequently, its complementary mask BEFGJ.",
            )}
          </p>
          <OrbitAtlas
            title={text(
              "Все 23 орбиты и квадрики 4/9",
              "All 23 orbits and quadrics for 4/9",
            )}
            families={FOUR_FAMILIES}
          />
        </section>

        <section>
          <h3>
            {text(
              "4. Общая параметризация квадрик 4/9",
              "4. Common parametrization of the 4/9 quadrics",
            )}
          </h3>
          <p>
            {text(
              "Для четырёх клеток исключение оставляет одну диагональную квадрику Σkᵢqᵢ²=0. Поскольку Σkᵢ=0, любая знаковая точка ε с координатами ±1 лежит на ней. Для направления u прямая через ε даёт универсальную проекционную формулу:",
              "For four cells, elimination leaves one diagonal quadric Σkᵢqᵢ²=0. Since Σkᵢ=0, every sign point ε with coordinates ±1 lies on it. For a direction u, the line through ε gives the universal projection formula:",
            )}
          </p>
          <Latex display>{String.raw`D=\sum_{i=1}^4k_iu_i^2,\quad L_\varepsilon=\sum_{i=1}^4k_i\varepsilon_i u_i,\quad q_i=D\varepsilon_i-2L_\varepsilon u_i`}</Latex>
          <Latex display>{String.raw`\sum k_iq_i^2=D^2\sum k_i\varepsilon_i^2-4DL_\varepsilon^2+4L_\varepsilon^2D=0`}</Latex>
          <p>
            {text(
              "Для полноты достаточно четырёх строк матрицы Адамара H. Если все kᵢ ненулевые и q* — ненулевая рациональная точка квадрики, то из det H=−16 следует, что хотя бы одно спаривание Lε(q*) ненулевое. В соответствующей карте подстановка u=q* даёт D=0 и q=−2Lε(q*)q*: получена та же проективная точка. После очистки знаменателей это даёт алгоритмическое покрытие целых решений.",
              "For completeness, the four rows of the Hadamard matrix H suffice. If every kᵢ is nonzero and q* is a nonzero rational point of the quadric, det H=−16 implies that at least one pairing Lε(q*) is nonzero. In that chart, setting u=q* gives D=0 and q=−2Lε(q*)q*, the same projective point. Clearing denominators gives algorithmic coverage of integral solutions.",
            )}
          </p>
          <Latex display>{String.raw`H=\begin{pmatrix}1&1&1&1\\1&1&-1&-1\\1&-1&1&-1\\1&-1&-1&1\end{pmatrix},\qquad \det H=-16`}</Latex>
          <p>
            {text(
              "Красная вырожденная квадрика имеет один нулевой коэффициент: соответствующий четвёртый корень свободен, а нетривиальная коника параметризуется отдельно и полностью:",
              "The degenerate red quadric has one zero coefficient: the corresponding fourth root is free, while the nontrivial conic is parametrized separately and completely:",
            )}
          </p>
          <Latex display>{String.raw`r=-a^2+2ab+b^2,\quad s=a^2+b^2,\quad t=a^2+2ab-b^2,\quad r^2+t^2=2s^2`}</Latex>
        </section>

        <section>
          <h3>{text("5. Цветовая дифференциация", "5. Color differentiation")}</h3>
          <p>
            {text(
              "Цвет закрепляется не за позицией клетки, а за конкретным уравнением левого ядра. В большой матрице цветом отмечаются только фактические квадратные значения; в миниатюре — опоры выбранных уравнений. Пересечение двух опор делит миниатюру на два цвета.",
              "A color belongs to a particular left-kernel equation, not to a fixed cell position. In the large matrix, color marks only values that are actually perfect squares; in a miniature, it marks the supports of the selected equations. A cell shared by two supports is split between their colors.",
            )}
          </p>
          <p>
            {text(
              "Цвета упорядочены по предпочтительности. При построении цветового базиса сначала выбирается доступное отношение наиболее предпочтительного типа:",
              "Colors are ordered by preference. When a colored basis is constructed, the available relation of the most preferred type is selected first:",
            )}
          </p>
          <Latex display>{String.raw`\mathrm{red}>\mathrm{yellow}>\mathrm{blue}>\mathrm{green}>\mathrm{brown}>\mathrm{dark\ gray}>\mathrm{light\ gray}`}</Latex>
          <p>
            {text(
              "Для маски 5/9 после первого отношения выбирается наиболее предпочтительное отношение, линейно независимое от него. При одинаковом цветовом приоритете дополнительная математическая иерархия не вводится. Разные оттенки одного цвета нужны только для визуального разделения равноправных групп и не несут отдельной семантики.",
              "For a 5/9 mask, the first relation is followed by the most preferred relation linearly independent of it. Equal color priority introduces no further mathematical hierarchy. Different shades of one color serve only to distinguish equal-status groups visually and carry no separate semantics.",
            )}
          </p>
          {proofs.map((proof) => (
            <section className="general-color-proof" key={proof.id}>
              <h4>{proof.title}</h4>
              <p>{proof.summary}</p>
              {proof.formulas.map((formula) => (
                <Latex display key={formula}>{formula}</Latex>
              ))}
              <p>{proof.conclusion}</p>
            </section>
          ))}
        </section>

        <section>
          <h3>{text("6. Переход от 4/9 к 5/9", "6. From 4/9 to 5/9")}</h3>
          <p>
            {text(
              "Пять клеток дают две независимые квадрики. Канонический цветовой базис выбирается среди отношений 4/9-подмасок по установленному выше цветовому приоритету: сначала лучшее доступное отношение, затем лучшее независимое от него. Любой другой базис того же двумерного левого ядра эквивалентен и не меняет ни множество решений, ни достаточность системы.",
              "Five cells give two independent quadrics. The canonical colored basis is selected among relations of the 4/9 submasks according to the color priority fixed above: first the best available relation, then the best one independent of it. Any other basis of the same two-dimensional left kernel is equivalent and changes neither the solution set nor sufficiency of the system.",
            )}
          </p>
          <Latex display>{String.raw`\ker L_S^T=\langle R_{\mathrm{color}_1},R_{\mathrm{color}_2}\rangle\quad\Longrightarrow\quad \{R_1(q^2)=R_2(q^2)=0\}\Longleftrightarrow\exists!\,(E,x,y)`}</Latex>
          <OrbitAtlas
            title={text(
              "Все 23 орбиты и пары квадрик 5/9",
              "All 23 orbits and pairs of quadrics for 5/9",
            )}
            families={FIVE_FAMILIES}
          />
          <p>
            {text(
              "Индивидуальное доказательство обязано не только проверить эти две квадрики, но и вывести совместную параметризацию корней. Полнота указывается отдельно: доказанная, алгоритмически восстанавливаемая через НОД и знаки либо пока неизвестная. Проверка тождества сама по себе полноты не доказывает.",
              "An individual proof must do more than verify the two quadrics: it must derive their joint root parametrization. Coverage is reported separately as proved, algorithmically recoverable through gcds and signs, or still unknown. Verifying an identity alone does not prove completeness.",
            )}
          </p>
          <div className="topic-actions">
            <Link className="button button-primary" to="/theory/arithmetic-progressions-dir">
              {text(
                "Далее: прогрессии квадратов и dir",
                "Next: progressions of squares and dir",
              )}{" "}
              <span>→</span>
            </Link>
            <Link className="button button-ghost" to="/lab">
              {text("Открыть атлас масок", "Open the pattern atlas")}
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}

function CommonProofPage() {
  const { locale, text } = useLocale();
  const { proofId } = useParams();
  const proof = commonProofById(proofId, locale);
  if (!proof) return <Navigate to="/about" replace />;
  const families = FAMILIES.filter((family) =>
    family.justifications.some((item) => item.commonProofId === proof.id),
  );

  return (
    <article className="page proof-page common-proof-page">
      <Link className="back-link" to="/lab">
        ← {text("К атласу", "Back to the atlas")}
      </Link>
      <header className="proof-page-header">
        <div>
          <p className="eyebrow">
            {text("Общая часть доказательства", "General proof chapter")}
          </p>
          <h1>{proof.title}</h1>
          <p>{proof.summary}</p>
        </div>
      </header>
      <div className="common-proof-layout">
        <section className="latex-card common-lemma-card">
          {proof.formulas.map((formula, index) => (
            <section className="latex-step" key={formula}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <Latex display>{formula}</Latex>
            </section>
          ))}
          <div className="lemma-conclusion">{proof.conclusion}</div>
          {proof.id === "arithmetic-progression" ? (
            <div className="topic-actions">
              <Link
                className="button button-primary"
                to="/theory/arithmetic-progressions-dir"
              >
                {text(
                  "Полная теория прогрессий и dir",
                  "Complete theory of progressions and dir",
                )}{" "}
                <span>→</span>
              </Link>
            </div>
          ) : null}
        </section>
        <aside className="lemma-families">
          <span>
            {text("Используют эту лемму", "Families using this lemma")} ·{" "}
            {families.length}
          </span>
          {families.map((family) => (
            <Link
              className={`lemma-family tone-${family.group}`}
              to={`${orbitPath(family.id)}#family-proof`}
              key={family.id}
            >
              <Pattern family={family} compact />
              <strong>{family.title}</strong>
              <i>→</i>
            </Link>
          ))}
        </aside>
      </div>
    </article>
  );
}

function NewsPage() {
  const { locale, text } = useLocale();
  const articles = news(locale);
  return (
    <div className="page text-page">
      <header className="editorial-header">
        <p className="eyebrow">{text("Публикации", "Publications")}</p>
        <h1>{text("Новости", "News")}</h1>
        <p>
          {text(
            "Вехи исследования и публикации проекта.",
            "Research milestones and project publications.",
          )}
        </p>
      </header>
      <div className="news-list">
        {articles.map((article, index) => (
          <NewsCard
            article={article}
            featured={index === 0}
            key={article.slug}
          />
        ))}
      </div>
    </div>
  );
}

function NewsCard({
  article,
  featured = false,
}: {
  article: NewsArticle;
  featured?: boolean;
}) {
  const { locale, text } = useLocale();
  return (
    <article className={`news-card ${featured ? "featured" : ""}`}>
      <div className="news-meta">
        <time dateTime={article.date}>
          {new Date(`${article.date}T00:00:00Z`).toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "UTC",
          })}
        </time>
        <span>{article.tags[0]}</span>
      </div>
      <h3>
        <Link to={`/news/${article.slug}`}>{article.title}</Link>
      </h3>
      <p>{article.summary}</p>
      <Link className="text-link" to={`/news/${article.slug}`}>
        {text("Читать заметку", "Read article")} <span>→</span>
      </Link>
    </article>
  );
}

function NewsArticlePage() {
  const { locale, text } = useLocale();
  const { slug } = useParams();
  const article = newsBySlug(slug, locale);
  if (!article) return <Navigate to="/news" replace />;
  return (
    <article className="page article-page">
      <Link className="back-link" to="/news">
        ← {text("Все новости", "All news")}
      </Link>
      <header>
        <div className="news-meta">
          <time>{article.date}</time>
          <span>{article.tags.join(" · ")}</span>
        </div>
        <h1>{article.title}</h1>
        <p>{article.summary}</p>
      </header>
      <div className="article-body">
        {article.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </article>
  );
}
/* START_FUNCTION about_page */
/* START_CONTRACT
PURPOSE: Render the localized project overview, research context, attribution, and redistribution terms.
CONTRACT: The visible canonical rights link follows the active locale's canonical host without altering the authored mathematical claims.
FAILURE_MEANING: Readers may receive an incorrect attribution target for the regional edition.
KEYWORDS: about, copyright, canonical, localization
COMPLEXITY: 4
END_CONTRACT */
function AboutPage() {
  const { locale, text } = useLocale();
  return (
    <article className="page proof-page about-page">
      <header className="proof-page-header">
        <div>
          <p className="eyebrow">{text("О проекте", "About")}</p>
          <h1>
            {text(
              "Исследование магических квадратов 3×3",
              "Research on 3×3 magic squares",
            )}
          </h1>
          <p>
            {text(
              "Проект посвящён арифметической и алгебраической структуре магических квадратов 3×3. Центральная открытая задача — существование магического квадрата из девяти положительных попарно различных полных квадратов; частичные задачи k/9, конгруумы и алгебра магических матриц рассматриваются как связанные направления одного исследования.",
              "The project studies the arithmetic and algebraic structure of 3×3 magic squares. Its central open question is whether a magic square can consist of nine positive, pairwise distinct perfect squares; the partial k/9 problems, congrua, and the algebra of magic matrices are treated as connected parts of the same investigation.",
            )}
          </p>
        </div>
      </header>

      <div className="proof-document about-document">
        <section>
          <h3>{text("1. Постановка задачи", "1. Problem statement")}</h3>
          <h4 className="about-subheading">
            {text("1.1. Магический квадрат", "1.1. Magic square")}
          </h4>
          <p>
            {text(
              "Магическим квадратом порядка n называется квадратная таблица из n² чисел, в которой суммы элементов каждой строки, каждого столбца и двух диагоналей, соединяющих противоположные углы, равны одному и тому же числу M — магической константе. Само определение не требует, чтобы элементы были различны; это условие вводится отдельно там, где оно необходимо.",
              "A magic square of order n is an n×n array of numbers in which the entries in every row, every column, and the two diagonals joining opposite corners have the same sum M, called the magic constant. Distinctness of the entries is not part of this definition; it is imposed separately when needed.",
            )}
          </p>
          <Latex display>{String.raw`\sum_{j=1}^{n}a_{ij}=M\ (1\le i\le n),\qquad
\sum_{i=1}^{n}a_{ij}=M\ (1\le j\le n),\qquad
\sum_{i=1}^{n}a_{ii}=\sum_{i=1}^{n}a_{i,n+1-i}=M.`}</Latex>
          <p>
            {text(
              "Магический квадрат порядка 3 состоит из девяти чисел. В явной форме условие означает равенство восьми сумм: трёх строк, трёх столбцов и двух диагоналей.",
              "A magic square of order 3 has nine entries. Explicitly, the condition is the equality of eight sums: three rows, three columns, and two diagonals.",
            )}
          </p>
          <Latex display>{String.raw`\begin{gathered}
\begin{pmatrix}a&b&c\\d&e&f\\g&h&j\end{pmatrix},\\[3pt]
a+b+c=d+e+f=g+h+j=M,\\
a+d+g=b+e+h=c+f+j=M,\\
a+e+j=c+e+g=M.
\end{gathered}`}</Latex>

          <h4 className="about-subheading">
            {text(
              "1.2. Квадратный квадрат и сильная задача 9/9",
              "1.2. A magic square of squares and the strong 9/9 problem",
            )}
          </h4>
          <p>
            {text(
              "Квадратным квадратом в этом проекте называется целочисленный магический квадрат порядка 3, все девять элементов которого являются положительными попарно различными полными квадратами. Иными словами, каждая клетка имеет вид q² для положительного целого q, и никакие две клетки не равны.",
              "In this project, a magic square of squares means an integral magic square of order 3 whose nine entries are positive, pairwise distinct perfect squares. Thus every entry has the form q² for a positive integer q, and no two entries are equal.",
            )}
          </p>
          <Latex display>{String.raw`a_{ij}=q_{ij}^{\,2},\qquad q_{ij}\in\mathbb Z_{>0},\qquad
(i,j)\ne(k,\ell)\ \Longrightarrow\ q_{ij}^{\,2}\ne q_{k\ell}^{\,2}.`}</Latex>
          <p>
            {text(
              "Сильная задача 9/9 состоит в том, чтобы построить такой квадрат либо доказать, что он не существует. Она остаётся открытой.",
              "The strong 9/9 problem is to construct such a square or prove that none exists. It remains open.",
            )}
          </p>

          <h4 className="about-subheading">
            {text(
              "1.3. Слабая задача 7/9",
              "1.3. The weak 7/9 problem",
            )}
          </h4>
          <p>
            {text(
              "Квадратом k/9 в этой задаче называется целочисленный магический квадрат порядка 3 с попарно различными элементами, в котором не менее k клеток являются положительными полными квадратами. На остальные клетки не накладывается условие быть или не быть квадратами. Поэтому решение 8/9 или 9/9 автоматически является решением задачи 7/9.",
              "In this problem, a k/9 square is an integral magic square of order 3 with pairwise distinct entries and at least k positive perfect-square entries. The remaining entries are not required either to be squares or to be nonsquares. Consequently, an 8/9 or 9/9 solution would automatically solve the 7/9 problem.",
            )}
          </p>
          <p>
            {text(
              "Известен один класс квадратов 7/9; существуют ли другие, неизвестно.",
              "One class of 7/9 squares is known; whether any others exist is unknown.",
            )}
          </p>
          <p>
            {text(
              "Представителем известного класса является квадрат Бремнера—Сэллоуза:",
              "A representative of the known class is the Bremner–Sallows square:",
            )}
          </p>
          <Latex display>{String.raw`\mathcal B=\begin{pmatrix}
565^2&289^2&373^2\\
23^2&425^2&137\cdot2633\\
151\cdot1471&527^2&205^2
\end{pmatrix}.`}</Latex>
          <p>
            {text(
              "Обозначим суммы строк через Rᵢ, столбцов через Cᵢ, а двух диагоналей через D₁ и D₂. Тогда точный сертификат магичности имеет вид ниже. Семь указанных корней попарно различны, а две оставшиеся клетки не являются квадратами:",
              "Let Rᵢ denote the row sums, Cᵢ the column sums, and D₁ and D₂ the two diagonal sums. The exact magic-square certificate is then the equality below. The seven displayed roots are pairwise distinct, while the other two entries are not squares:",
            )}
          </p>
          <Latex display>{String.raw`R_1=R_2=R_3=C_1=C_2=C_3=D_1=D_2=541875,`}</Latex>
          <Latex display>{String.raw`600^2<137\cdot2633=360721<601^2,\qquad
471^2<151\cdot1471=222121<472^2.`}</Latex>
          <p>
            {text(
              "Повороты, отражения и общее умножение всех клеток на один и тот же положительный полный квадрат дают лишь тривиально эквивалентные варианты и не считаются новым решением.",
              "Rotations, reflections, and multiplication of every entry by the same positive perfect square produce only trivially equivalent variants and do not count as a new solution.",
            )}
          </p>
          <p>
            {text(
              "Между постановками есть точные логические импликации. Любой квадрат 9/9 был бы новым решением 7/9. Доказательство единственности известного класса 7/9 исключило бы также 8/9 и 9/9, тогда как доказательство несуществования 9/9 само по себе не решило бы задачу единственности 7/9.",
              "There are exact logical implications between the two formulations. Any 9/9 square would be a new 7/9 solution. A proof that the known 7/9 class is unique would also rule out 8/9 and 9/9 squares, whereas a proof of nonexistence for 9/9 alone would not settle uniqueness for 7/9.",
            )}
          </p>
          <ul className="proof-references">
            <li>
              <a href="https://www.impan.pl/en/publishing-house/journals-and-series/acta-arithmetica/all/88/3/110732/on-squares-of-squares">
                A. Bremner, <em>On squares of squares</em>, Acta Arithmetica 88 (1999), 289–297
              </a>
            </li>
            <li>
              <a href="https://doi.org/10.4064/aa99-3-6">
                A. Bremner, <em>On squares of squares II</em>, Acta Arithmetica 99 (2001), 289–308
              </a>
            </li>
            <li>
              <a href="https://link.springer.com/article/10.1007/s40993-025-00671-5">
                {text(
                  "Современная статья Research in Number Theory (2025), формулирующая задачу 3×3 как открытую",
                  "A 2025 Research in Number Theory article recording the 3×3 problem as open",
                )}
              </a>
            </li>
            <li>
              <a href="https://www.multimagie.com/English/SquaresOfSquares.htm">
                {text(
                  "Каталог квадратов из квадратов Multimagie",
                  "Multimagie catalog of magic squares of squares",
                )}
              </a>
            </li>
            <li>
              <a href="https://oeis.org/A221669">
                OEIS A221669
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h3>{text("2. Область исследования", "2. Scope of the research")}</h3>
          <p>
            {text(
              "Задачи 9/9 и 7/9 задают основную цель, но не исчерпывают проект. Мы изучаем, какие наборы клеток магического квадрата могут одновременно быть полными квадратами, строим параметрические семейства таких квадратов, исследуем ограничения делимости и представления чисел суммами квадратов, а также рассматриваем обычное матричное умножение магических и полумагических квадратов.",
              "The 9/9 and 7/9 problems provide the main objective, but they do not exhaust the project. We study which sets of entries can simultaneously be perfect squares, construct parametric families of such squares, investigate divisibility restrictions and representations by sums of squares, and study ordinary matrix multiplication of magic and semimagic squares.",
            )}
          </p>
          <p>
            {text(
              "Обозначение k/9 означает, что выбранные k клеток гарантированно являются квадратами; остальные клетки также могут случайно оказаться квадратами. Два расположения выбранных клеток считаются одинаковыми, если одно получается из другого поворотом или отражением всего квадрата.",
              "The notation k/9 means that the selected k entries are guaranteed to be squares; some of the remaining entries may also happen to be squares. Two placements of selected entries are regarded as the same when one is obtained from the other by rotating or reflecting the whole square.",
            )}
          </p>
        </section>

        <section>
          <h3>{text("3. Основные результаты", "3. Main results")}</h3>
          <p>
            {text(
              "Ниже перечислены основные результаты проекта. Для каждого результата указаны точный объём доказанного утверждения, его ограничения и ссылки на подробный вывод.",
              "The principal results of the project are listed below. Each result is accompanied by the precise scope of the proved statement, its limitations, and links to the detailed derivation.",
            )}
          </p>

          <h4 className="about-subheading">
            {text("3.1. Полное покрытие позиционных типов 5/9", "3.1. Complete coverage of the 5/9 positional types")}
          </h4>
          <p>
            {text(
              "Для каждого из 23 существенно различных расположений пяти квадратных клеток построено явное невырожденное параметрическое семейство целочисленных магических квадратов. Поэтому ни один позиционный тип 5/9 не остаётся без конструкции. Под невырожденностью здесь понимаются положительность и попарная различность всех девяти элементов хотя бы для допустимых значений параметров семейства.",
              "For each of the 23 essentially different placements of five square entries, we construct an explicit nondegenerate parametric family of integral magic squares. Thus no 5/9 positional type remains without a construction. Here nondegenerate means that admissible parameter values exist for which all nine entries are positive and pairwise distinct.",
            )}
          </p>
          <p>
            {text(
              "Для каждой выписанной формулы отдельно указана покрываемая часть рациональных решений. В маске ABCDG одна полиномиальная формула также покрывает лишь подсемейство, однако отдельный алгоритм по коническому расслоению перечисляет уже все рациональные решения и имеет явный обратный ход. Поэтому полноту формулы и полноту эффективного алгоритма следует различать.",
              "The subset of rational solutions covered by each displayed formula is stated separately. For the ABCDG pattern, one polynomial formula likewise covers only a subfamily, but a separate conic-bundle algorithm enumerates every rational solution and has an explicit inverse. Completeness of a formula and completeness of an effective algorithm must therefore be distinguished.",
            )}
          </p>
          <ul className="proof-references">
            <li><Link to="/orbits/5">{text("Атлас позиционных типов 5/9", "Atlas of the 5/9 positional types")}</Link></li>
            <li><Link to="/orbits/5/abcdg">{text("Полный алгоритм ABCDG", "Complete ABCDG algorithm")}</Link></li>
            <li><Link to="/orbits/6">{text("Атлас позиционных типов 6/9", "Atlas of the 6/9 positional types")}</Link></li>
            <li><Link to="/orbits/7">{text("Тригонометрический атлас 7/9", "Trigonometric atlas of the 7/9 positional types")}</Link></li>
            <li><Link to="/proofs/general">{text("Общая теория частичных квадратных масок", "General theory of partial square patterns")}</Link></li>
          </ul>

          <h4 className="about-subheading">
            {text("3.2. Продвижение для 6/9", "3.2. Progress on 6/9")}
          </h4>
          <p>
            {text(
              "Все 16 позиционных типов 6/9 имеют точные системы уравнений, отдельные статьи, бесконечные рациональные семейства и положительные специализации ровно 6/9. Для двух параллельных типов доказана полная tfmn-классификация всех невырожденных рациональных решений. Четырнадцать непараллельных типов сведены к выбранным расслоениям рода 1 и моделям эллиптических K3-поверхностей; предъявленные неторсионные секции дают бесконечные семейства. Для большинства этих K3 точный геометрический ранг и полнота выбранной рациональной карты не установлены.",
              "All 16 positional 6/9 types have exact systems of equations, dedicated articles, infinite rational families, and positive specializations of exact type 6/9. For the two parallel types, a complete tfmn classification of all nondegenerate rational solutions is proved. The fourteen nonparallel types are reduced through chosen genus-one fibrations to elliptic K3 models; explicit non-torsion sections give infinite families. For most of these K3 surfaces, the exact geometric rank and global completeness of the chosen rational chart remain undetermined.",
            )}
          </p>
          <p>
            {text(
              "Бремнер представил все шестнадцать конфигураций 6/9 как пересечения трёх квадрик в ℙ⁵ и для каждой связанной поверхности указал эллиптическое расслоение, дающее однопараметрическое бесконечное семейство. Для одной конфигурации он доказал, что поверхность является гладкой K3, нашёл особые слои с паспортом 4I₄+4I₂ и вычислил ранг 2 над полем функций и ранг 20 решётки Нерона—Севери. Настоящий атлас приводит явные модели и доказанные характеристики всех шестнадцати масок; для двух параллельных масок дополнительно получена полная tfmn-классификация невырожденных рациональных решений.",
              "Bremner represented all sixteen 6/9 configurations as intersections of three quadrics in ℙ⁵ and identified on each associated surface an elliptic fibration that yields an infinite one-parameter family. For one configuration he proved that the surface is a smooth K3, found the singular fibers with passport 4I₄+4I₂, and computed rank 2 over the function field and rank 20 for the Néron–Severi lattice. The present atlas records explicit models and proved characteristics for all sixteen patterns; for the two parallel patterns it additionally gives a complete tfmn classification of nondegenerate rational solutions.",
            )}
          </p>

          <h4 className="about-subheading">
            {text("3.3. Магические, чарующие и полумагические квадраты", "3.3. Magic, charming, and semimagic squares")}
          </h4>
          <p>
            {text(
              "Произведение двух магических квадратов 3×3 обычно уже не является магическим, но остаётся полумагическим: суммы во всех строках и столбцах равны. Такие произведения образуют выделенный трёхмерный класс, который мы называем чарующими квадратами. Магические и чарующие квадраты чередуются при умножении: произведение двух магических квадратов чарующее, произведение магического и чарующего — магическое, а произведение двух чарующих — чарующее.",
              "The product of two 3×3 magic squares is generally no longer magic, but it remains semimagic: all row and column sums are equal. Such products form a distinguished three-dimensional class that we call charming squares. Magic and charming squares alternate under multiplication: magic times magic is charming, magic times charming is magic, and charming times charming is charming.",
            )}
          </p>
          <p>
            {text(
              "После отделения общего скалярного компонента эти два класса задают чётную и нечётную части пятимерной ассоциативной алгебры полумагических матриц. Для этой алгебры получены явные формулы определителя, присоединённой матрицы и характеристического многочлена.",
              "After the common scalar component is separated, these two classes give the even and odd parts of the five-dimensional associative algebra of semimagic matrices. Explicit formulas for the determinant, adjugate, and characteristic polynomial have also been obtained for this algebra.",
            )}
          </p>
          <p>
            {text(
              "Более того, над рациональными числами каждый полумагический квадрат 3×3 допускает явное разложение в сумму магического квадрата и произведения двух магических квадратов:",
              "Moreover, over the rational numbers every 3×3 semimagic square has an explicit decomposition as the sum of a magic square and a product of two magic squares:",
            )}
          </p>
          <Latex display>{String.raw`S(E,x,y,z,w)=M(E,x,y)+M(0,1,0)M(0,z,-w).`}</Latex>
          <p>
            {text(
              "Второе слагаемое является чарующим квадратом C(0,z,w). Поэтому магические и чарующие квадраты не только порождают всё пятимерное пространство линейными комбинациями: для каждого его элемента разложение выписывается непосредственно.",
              "The second term is the charming square C(0,z,w). Thus magic and charming squares do more than span the five-dimensional space by linear combinations: the decomposition of every element is explicit.",
            )}
          </p>

          <h4 className="about-subheading">
            {text("3.4. Конгруумы и поверхность F4+", "3.4. Congrua and the F4+ surface")}
          </h4>
          <p>
            {text(
              "Три квадрата в арифметической прогрессии определяют прямоугольный треугольник, площадь которого равна шагу прогрессии. Поэтому часть задачи о магических квадратах связана с классической задачей о конгруэнтных числах. Если положить F(a,b)=ab(a²−b²), то значения F, отличающиеся на рациональный квадрат, соответствуют одному классу этой задачи.",
              "Three squares in arithmetic progression determine a right triangle whose area equals the common difference. This connects part of the magic-square problem with the classical congruent number problem. If F(a,b)=ab(a²−b²), then values of F that differ by a rational square represent the same class in this problem.",
            )}
          </p>
          <Latex display>{String.raw`F(a,b)=\rho^2F(a,d)`}</Latex>
          <p>
            {text(
              "В проекте введена полная F4+-модель пар пифагоровых площадей, отличающихся на рациональный квадрат. Её вспомогательная рациональная эллиптическая поверхность имеет точный ранг 2, а две пифагоровы параметризации задают две независимые точки на универсальной кривой конгруэнтного числа над полем функций F4+. Следовательно, эта кривая имеет ранг не меньше 2. Конструкция даёт единый способ получать тождества между конгруумами и связанные с ними семейства магических квадратов, в том числе некоторые семейства 6/9.",
              "The project introduces the complete F4+ model for pairs of Pythagorean areas differing by a rational square. Its auxiliary rational elliptic surface has exact rank 2, while the two Pythagorean parametrizations define two independent points on the universal congruent-number curve over the function field of F4+. Consequently, that curve has rank at least 2. The construction gives a unified way to obtain identities between congrua and related families of magic squares, including some 6/9 families.",
            )}
          </p>
          <ul className="proof-references">
            <li>
              <Link to="/proofs/arithmetic-progression">
                {text(
                  "Три квадрата в арифметической прогрессии",
                  "Three squares in arithmetic progression",
                )}
              </Link>
            </li>
            <li>
              <Link to="/theory/f4-plus">
                {text(
                  "F4+: полная модель и теорема о ранге",
                  "F4+: complete model and rank theorem",
                )}
              </Link>
            </li>
          </ul>
        </section>

        <section id="copyright" className="copyright-section">
          <h3>
            {text(
              "4. Авторы, использование ИИ и распространение",
              "4. Authors, use of AI, and redistribution",
            )}
          </h3>
          <h4>{text("Авторы", "Authors")}</h4>
          <ul className="rights-list">
            <li>
              {text(
                "Владимир Мынка — основной автор.",
                "Vladimir Mynka — lead author.",
              )}
            </li>
            <li>
              {text(
                "Автор, пожелавший сохранить анонимность, — соавтор.",
                "An author who wishes to remain anonymous — co-author.",
              )}
            </li>
            <li>
              {text(
                "Алексей Поздеев — соавтор.",
                "Alexey Pozdeev — co-author.",
              )}
            </li>
          </ul>
          <h4>{text("Благодарности", "Acknowledgements")}</h4>
          <p>
            {text(
              "Отдельная благодарность Алексею Халину (Институт проблем передачи информации им. А. А. Харкевича РАН, ИППИ РАН) за консультацию и ранние проверки, а также активным участникам локального математического сообщества.",
              "Special thanks to Alexey Khalin (Kharkevich Institute for Information Transmission Problems of the Russian Academy of Sciences, IITP RAS) for consultation and early checks, and to the active participants in the local mathematics community.",
            )}
          </p>
          <h4 id="ai-use">
            {text(
              "Использование искусственного интеллекта",
              "Use of artificial intelligence",
            )}
          </h4>
          <p>
            {text(
              "Математические результаты, представленные на сайте, за исключением случаев, явно отмеченных в хронологии, получены человеческими авторами, перечисленными выше.",
              "The mathematical results presented on this site, except where explicitly noted in the timeline, were obtained by the human authors listed above.",
            )}
          </p>
          <p>
            {text(
              "Искусственный интеллект использовался:",
              "Artificial intelligence was used:",
            )}
          </p>
          <ul className="rights-list">
            <li>
              {text(
                "при подготовке и редактировании окончательных текстов;",
                "to prepare and edit the final texts;",
              )}
            </li>
            <li>
              {text(
                "для реконструкции математических утверждений и доказательств по внутренним заметкам и переписке авторов;",
                "to reconstruct mathematical statements and proofs from the authors’ internal notes and correspondence;",
              )}
            </li>
            <li>
              {text(
                "для интеллектуального поиска по редкой и слабо индексируемой научной литературе, сопоставления независимо полученных результатов с известными работами и приведения терминологии к общепринятой. Обычный библиографический поиск здесь затруднён самой математической формой материала: формулы плохо поддаются поиску, а понятия, независимо введённые в ходе исследования, первоначально ещё не были связаны с терминологией существующей литературы.",
                "for assisted search across rare and poorly indexed scientific literature, comparison of independently obtained results with known work, and alignment with established terminology. Conventional bibliographic search is hindered here by the mathematical form of the material itself: formulas are difficult to search, while concepts introduced independently during the research were not initially connected to the terminology used in the existing literature.",
              )}
            </li>
          </ul>
          <p>
            {text(
              "Все опубликованные материалы неоднократно вычитывались основным автором. Вместе с тем редакционные исправления также вносились посредством ИИ как инструмента исполнения. Поэтому автоматические системы могут классифицировать тексты сайта как сгенерированные искусственным интеллектом; такая классификация сама по себе не отражает происхождение изложенных математических результатов.",
              "All published materials were read and reviewed repeatedly by the lead author. Editorial corrections were nevertheless also applied through AI as an execution tool. Automated systems may therefore classify the site’s texts as AI-generated; such a classification does not by itself reflect the origin of the mathematical results presented.",
            )}
          </p>
          <p>
            {text(
              "Если искусственный интеллект участвовал непосредственно в содержательной части исследования, это отмечается отдельно в хронологии с указанием использованной модели и распределения работы между человеком и ИИ.",
              "Whenever artificial intelligence contributed directly to the substantive research, the timeline records this separately, naming the model used and explaining the division of work between the human and AI participants.",
            )}
          </p>
          <h4>Copyright</h4>
          <p>
            {text(
              "Copyright © 2021–2026 Владимир Мынка.",
              "Copyright © 2021–2026 Vladimir Mynka.",
            )}
          </p>
          <p>
            {text(
              "Материалы проекта разрешается копировать и распространять только при явном указании авторства и сохранении активной ссылки на канонический сайт проекта. Иное использование требует отдельного согласования с автором.",
              "Project materials may be copied and redistributed only when authorship is explicitly credited and an active link to the canonical project site is retained. Other uses require separate permission from the author.",
            )}
          </p>
          <a className="canonical-rights-link" href={siteOriginForLocale(locale)}>
            {new URL(siteOriginForLocale(locale)).host}
          </a>
          <p>
            <Link to="/timeline">
              {text(
                "Хронология исследования",
                "Research timeline",
              )}{" "}
              →
            </Link>
          </p>
        </section>
      </div>
    </article>
  );
}
/* END_FUNCTION about_page */

function NotFoundPage() {
  const { text } = useLocale();
  return (
    <div className="page text-page not-found-page">
      <header className="editorial-header">
        <p className="eyebrow">404</p>
        <h1>{text("Страница не найдена", "Page not found")}</h1>
        <p>
          {text(
            "Такого маршрута нет в атласе доказательств.",
            "This route does not exist in the proof atlas.",
          )}
        </p>
        <Link className="button button-primary" to="/">
          {text("На главную", "Go to the home page")}
        </Link>
      </header>
    </div>
  );
}

export function App() {
  const localizedRoutes = (
    <>
      <Route index element={<HomePage />} />
        <Route path="theory" element={<TheoryIndexPage />} />
        <Route path="squares-of-squares" element={<SquaresOfSquaresPage />} />
        <Route path="theory/magic-squares-3x3" element={<BasicMagicTheoryPage />} />
        <Route path="theory/residues" element={<ResiduesTheoryPage />} />
        <Route path="theory/prime-divisors" element={<PrimeDivisorsTheoryPage />} />
        <Route
          path="theory/arithmetic-progressions-dir"
          element={<DirProgressionsTheoryPage />}
        />
        <Route
          path="theory/6-9-patterns"
          element={<SixNinePatternsTheoryPage />}
        />
        <Route
          path="theory/7-9-patterns"
          element={<SevenNinePatternsTheoryPage />}
        />
        <Route path="theory/fmn-tfmn" element={<FmnTfmnTheoryPage />} />
        <Route
          path="theory/early-tf-families"
          element={<EarlyTfFamiliesTheoryPage />}
        />
        <Route path="theory/f7-plus" element={<F7PlusTheoryPage />} />
        <Route path="theory/tfmn-forms" element={<TfmnContainerFormsTheoryPage />} />
        <Route
          path="theory/elliptic-surfaces-primer"
          element={<EllipticSurfacesPrimerTheoryPage />}
        />
        <Route path="theory/f4-plus" element={<F4PlusTheoryPage />} />
        <Route
          path="theory/tf-pair-generation"
          element={<TfPairGenerationTheoryPage />}
        />
        <Route path="theory/f9-plus" element={<F9PlusTheoryPage />} />
        <Route
          path="theory/f9-plus-elliptic-layers"
          element={<F9EllipticLayersTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdeh"
          element={<AbcdehSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdej"
          element={<AbcdejSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdfh"
          element={<AbcdfhSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdhj"
          element={<AbcdhjSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcegh-abcegj"
          element={<AbceCommonSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abdefh"
          element={<AbdefhSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abdefj"
          element={<AbdefjSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abefgh"
          element={<AbefghSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdef"
          element={<AbcdefSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abefgj-abdfhj"
          element={<ParallelTfmnKummerTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdeg"
          element={<AbcdegSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdfg"
          element={<AbcdfgSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcdgj"
          element={<AbcdgjSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/abcghj"
          element={<AbcghjSurfaceTheoryPage />}
        />
        <Route
          path="theory/6-9/synthesis"
          element={<SixNineSynthesisTheoryPage />}
        />
        <Route
          path="theory/matrix-algebra/magic-charming-semimagic"
          element={<SemimagicAlgebraTheoryPage />}
        />
        <Route
          path="theory/matrix-algebra/block-structure-split-quaternions"
          element={<SemimagicStructureTheoryPage />}
        />
        <Route path="lab" element={<LabPage />} />
        <Route path="lab/6" element={<SixNineLabPage />} />
        <Route path="families/:familyId" element={<FamilyRedirect />} />
        <Route path="orbits/4" element={<OrbitLevelPage level={4} />} />
        <Route path="orbits/5" element={<OrbitLevelPage level={5} />} />
        <Route path="orbits/6" element={<SixNineAtlasPage />} />
        <Route path="orbits/7" element={<SevenNineAtlasPage />} />
        <Route path="orbits/:level/:familyId" element={<OrbitFamilyPage />} />
        <Route path="proofs/general" element={<GeneralTheoryPage />} />
        <Route path="proofs/:proofId" element={<CommonProofPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="news/:slug" element={<NewsArticlePage />} />
        <Route path="timeline" element={<TimelinePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="*" element={<NotFoundPage />} />
    </>
  );
  return (
    <Routes>
      {SINGLE_LOCALE ? (
        <Route path="/" element={<LocaleLayout />}>
          {localizedRoutes}
        </Route>
      ) : (
        <Route path="/:locale" element={<LocaleLayout />}>
          {localizedRoutes}
        </Route>
      )}
      <Route path="*" element={<LegacyLocaleRedirect />} />
    </Routes>
  );
}
